import { EmailBlockData } from '../ModernEmailBuilder';
import { getPlatformData } from './socialIcons';
import { EmailTemplateStyles } from '../../../types/emailBuilder';
import { escapeHtml } from './helpers';
import { buildQrCodeDataUrlForEmail, normalizeHexColorForQr } from './qrEmailEmbed';
import { renderComplianceFooterHTML, resolveComplianceFooterTextColor } from './complianceFooter';
import { EDITORIAL_RESPONSIVE_CSS, editorialMobile } from './editorialResponsiveCss';
import { HEADER_RESPONSIVE_CSS } from './headerResponsiveCss';
import { renderHeaderBlockHtml } from './headerBlockHtml';
import { MENU_RESPONSIVE_CSS } from './menuResponsiveCss';
import { renderMenuBlockHtml } from './menuBlockHtml';
import { HERO_CONTENT_RESPONSIVE_CSS } from './heroContentResponsiveCss';
import { renderHeroContentBlockHtml } from './heroContentBlockHtml';
import { renderFooterBlockHtml } from './footerBlockHtml';
import { resolveFooterStyles } from './footerBlockUtils';
import { FOOTER_EMAIL_BASE_CSS, FOOTER_RESPONSIVE_CSS } from './footerResponsiveCss';
import {
  isSvgStickerContent,
  resolveImageLinkType,
  resolveVideoThumbnailUrl,
  videoPlayButtonOverlayHtml,
} from './mediaBlockUtils';
import {
  getFeaturesGridColumns,
  normalizeFeaturesGridContent,
  resolveFeaturesGridVariant,
} from './featuresGridUtils';
import { renderButtonBlockHtml } from './buttonBlockHtml';
import { renderTestimonialHtml } from './testimonialHtml';
import {
  getFeaturesGridBodyPadding,
  getFeaturesGridLayoutMeta,
  getFeaturesGridTitlePadding,
  renderFeaturesGridFeatureEmailHtml,
} from './featuresGridLayouts';
import { isEffectivelyZeroPadding } from './textBlockTypography';
import {
  renderHeroOverlayButtonHtml,
  resolveHeroOverlayButtonConfig,
} from './heroOverlayButton';

type TextColorRange = {
  start: number;
  end: number;
  color: string;
};

const normalizeColorRanges = (
  ranges: TextColorRange[] | undefined,
  textLength: number
): TextColorRange[] => {
  if (!Array.isArray(ranges) || textLength <= 0) return [];
  const cleaned: TextColorRange[] = [];
  ranges.forEach((range) => {
    if (!range || typeof range.color !== 'string') return;
    const color = range.color.trim();
    if (!color) return;
    const rawStart = typeof range.start === 'number' ? range.start : 0;
    const rawEnd = typeof range.end === 'number' ? range.end : textLength;
    const start = Math.max(0, Math.min(rawStart, textLength));
    const end = Math.max(start, Math.min(rawEnd, textLength));
    if (end <= start) return;
    cleaned.push({ start, end, color });
  });
  cleaned.sort((a, b) => {
    if (a.start === b.start) {
      return a.end - b.end;
    }
    return a.start - b.start;
  });
  const merged: TextColorRange[] = [];
  cleaned.forEach((current) => {
    const last = merged[merged.length - 1];
    if (last && last.color === current.color && current.start <= last.end) {
      last.end = Math.max(last.end, current.end);
    } else {
      merged.push({ ...current });
    }
  });
  return merged;
};

const buildActionHref = (linkType?: string, linkUrl?: string) => {
  // Handle 'none' linkType - no link action
  if (linkType === 'none') return '';
  const trimmed = (linkUrl || '').trim();
  if (!trimmed) return '';
  if (linkType === 'mailto') {
    return trimmed.toLowerCase().startsWith('mailto:') ? trimmed : `mailto:${trimmed}`;
  }
  if (linkType === 'tel') {
    const normalized = trimmed.replace(/\s+/g, '');
    return normalized.toLowerCase().startsWith('tel:') ? normalized : `tel:${normalized}`;
  }
  return trimmed;
};

// Social link branding type
interface BrandingSocialLink {
  platform: string;
  url: string;
}

// Branding options for email generation
interface WorkspaceBrandingOptions {
  primaryColor?: string;
  secondaryColor?: string;
  socialLinks?: BrandingSocialLink[];
  emailSignature?: string;
  removeScubamailBranding?: boolean;
}

interface GenerateEmailHTMLOptions {
  /** Origin for resolving /public image paths in sent emails (defaults to app host). */
  assetOrigin?: string;
  enableInteractiveVideo?: boolean;
  isMobile?: boolean;
  // Poll tracking context - when provided, generates tracking URLs
  pollTracking?: {
    workspaceId: string;
    campaignId: string;
    baseUrl?: string; // Cloud function base URL, defaults to production
  };
  // Workspace branding - when provided, appends social links and signature
  branding?: WorkspaceBrandingOptions;
  address?: string | string[];
  hideBranding?: boolean;
  previewText?: string;
}

// Generate social icons HTML from workspace branding social links
function generateBrandingSocialLinksHTML(
  socialLinks: BrandingSocialLink[],
  primaryColor: string = '#3B82F6'
): string {
  if (!socialLinks || socialLinks.length === 0) return '';

  const platformIcons: Record<string, string> = {
    facebook: 'f',
    twitter: '𝕏',
    x: '𝕏',
    instagram: '📷',
    linkedin: 'in',
    youtube: '▶',
    tiktok: '♪',
    pinterest: 'P',
    website: '🌐',
    other: '●',
  };

  const socialIconsHtml = socialLinks.map((link) => {
    const platformKey = link.platform.toLowerCase();
    const icon = platformIcons[platformKey] || platformIcons['other'];
    const safeUrl = link.url.startsWith('http') ? link.url : `https://${link.url}`;
    return `
      <td style="padding: 0 6px;">
        <a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="display: inline-block; width: 36px; height: 36px; background-color: ${primaryColor}; border-radius: 50%; text-align: center; line-height: 36px; color: #FFFFFF; text-decoration: none; font-size: 16px;" title="${link.platform}">${icon}</a>
      </td>
    `;
  }).join('');

  return `
    <div style="padding: 20px 0; text-align: center;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center" style="margin: 0 auto; width: auto !important; table-layout: auto !important;">
        <tr>
          ${socialIconsHtml}
        </tr>
      </table>
    </div>
  `;
}

// Generate email signature HTML from workspace branding
function generateEmailSignatureHTML(signature: string): string {
  if (!signature || !signature.trim()) return '';

  // Simple markdown-like processing for basic styling
  let html = signature
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') // Bold
    .replace(/\*(.+?)\*/g, '<em>$1</em>') // Italic
    .replace(/\n/g, '<br>'); // Line breaks

  return `
    <div style="padding: 20px; text-align: left; font-size: 14px; color: #6B7280; line-height: 1.6; border-top: 1px solid #E5E7EB; margin-top: 20px;">
      ${html}
    </div>
  `;
}

// Helper to find all fonts used in blocks
function collectUsedFonts(blocks: EmailBlockData[], styles: EmailTemplateStyles): string[] {
  const fonts = new Set<string>();

  const normalizeFont = (value: unknown): string => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    // Take the primary family name (strip fallbacks and quotes)
    const primary = raw.split(',')[0]?.trim() || '';
    return primary.replace(/^['"]|['"]$/g, '').trim();
  };

  // Add base font from global styles
  if (styles.fontFamily) fonts.add(normalizeFont(styles.fontFamily));

  // Always include Inter and Playfair Display as they are used in many core templates as base/accent fonts
  fonts.add('Inter');
  fonts.add('Playfair Display');

  // Scans for font properties in blocks
  blocks.forEach(block => {
    if (block.styles) {
      if (block.styles.fontFamily) fonts.add(normalizeFont(block.styles.fontFamily));
      if (block.styles.productNameFontFamily) fonts.add(normalizeFont(block.styles.productNameFontFamily));
      if (block.styles.productPriceFontFamily) fonts.add(normalizeFont(block.styles.productPriceFontFamily));
      if (block.styles.buttonStyle?.fontFamily) fonts.add(normalizeFont(block.styles.buttonStyle.fontFamily));
      if (block.styles.headingFontFamily) fonts.add(normalizeFont(block.styles.headingFontFamily));
      if (block.styles.secondaryFontFamily) fonts.add(normalizeFont(block.styles.secondaryFontFamily));
    }
    if (block.content?.overlayText?.fontFamily) {
      fonts.add(normalizeFont(block.content.overlayText.fontFamily));
    }
  });

  const systemFonts = ['Arial', 'Helvetica', 'Verdana', 'Tahoma', 'Trebuchet MS', 'Century Gothic', 'Times New Roman', 'Georgia', 'Palatino', 'Garamond', 'Bookman', 'Impact', 'Comic Sans MS', 'Courier New', 'Lucida Console', 'Monaco', 'Consolas', 'system-ui', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI'];

  return Array.from(fonts).filter(f => f && !systemFonts.includes(f));
}

// Generate Google Fonts URL for used fonts
function getGoogleFontsLink(fonts: string[]): string {
  if (fonts.length === 0) return '';

  // Standard fonts that support multiple weights
  const standardFonts = ['Inter', 'Roboto', 'Open Sans', 'Lato', 'Poppins', 'Montserrat', 'Nunito', 'Raleway', 'Playfair Display'];

  const familyString = fonts.map(f => {
    const encoded = encodeURIComponent(f);
    if (standardFonts.includes(f)) {
      return `family=${encoded}:wght@400;500;600;700;800;900`;
    }
    // For other fonts (especially script/handwriting), request a limited set to avoid API errors
    // requesting 400 and 700 is a safe default that works for 99% of Google Fonts
    return `family=${encoded}:wght@400;700`;
  }).join('&');

  return `<link href="https://fonts.googleapis.com/css2?${familyString}&display=swap" rel="stylesheet">`;
}

// Helper to ensure font families with spaces are properly quoted for CSS
function formatFontFamily(fontFamily: string | undefined | null, defaultFont = 'Arial, sans-serif'): string {
  if (!fontFamily) return defaultFont;
  if (fontFamily.includes("'") || fontFamily.includes('"') || fontFamily.includes(',')) {
    return fontFamily;
  }
  if (fontFamily.includes(' ')) {
    return `'${fontFamily}', sans-serif`;
  }
  return `${fontFamily}, sans-serif`;
}

export function generateEmailHTML(
  blocks: EmailBlockData[],
  styles: EmailTemplateStyles,
  options: GenerateEmailHTMLOptions = {}
): string {
  const isMobile = options.isMobile === true;
  const containerWidth = isMobile ? '100%' : (styles.containerWidth || '600px');
  const containerMaxWidth = isMobile ? '480px' : (styles.containerWidth || '600px');
  const backgroundColor = styles.backgroundColor || '#ffffff';
  const fontFamily = formatFontFamily(styles.fontFamily);
  const contentBackgroundColor = styles.contentBackgroundColor || '#ffffff';
  const textColor = styles.textColor || '#111827';

  // Preview Text HTML (Hidden but visible in inbox preview)
  const previewTextHtml = options.previewText
    ? `
    <div style="display: none; max-height: 0px; overflow: hidden;">
      ${escapeHtml(options.previewText)}
    </div>
    <div style="display: none; max-height: 0px; overflow: hidden;">
      &nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;
    </div>
    `
    : '';
  const blockHTML = blocks
    .filter((block) => !block.hidden)
    .map((block) => wrapBlock(block.id, renderBlockToHTML(block, options, fontFamily)))
    .join('\n');
  const responsiveCss = buildResponsiveCss(blocks);

  // Generate branding content if provided
  let brandingHTML = '';
  if (options.branding) {
    const { socialLinks, emailSignature, primaryColor } = options.branding;

    // Add social links from branding
    if (socialLinks && socialLinks.length > 0) {
      brandingHTML += generateBrandingSocialLinksHTML(socialLinks, primaryColor || '#3B82F6');
    }

    // Add email signature from branding (if not already present as a block)
    const hasSignatureBlock = blocks.some(b => b.isSignature);
    if (emailSignature && emailSignature.trim() && !hasSignatureBlock) {
      brandingHTML += generateEmailSignatureHTML(emailSignature);
    }
  }

  const canRemoveBranding = options.hideBranding === true;
  const showAddress = styles.showAddress !== false;
  const showBadge = canRemoveBranding ? (styles.showBadge !== false) : true;

  const addressHtml = options.address ? (
    Array.isArray(options.address) ? options.address.join('<br/>') : options.address
  ) : '<span style="color: #EF4444; font-weight: bold;">[Missing mandatory physical address]</span>';

  const footerTextColor = resolveComplianceFooterTextColor(styles);

  // Collect all used fonts and generate dynamic link
  const usedFonts = collectUsedFonts(blocks, styles);
  const dynamicFontsLink = getGoogleFontsLink(usedFonts);
  // Add default fonts that are frequently used in templates (standard system font fallback)
  const defaultGoogleFontsLink = usedFonts.length === 0 ? '<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap" rel="stylesheet">' : '';

  if (showAddress || showBadge) {
    brandingHTML += renderComplianceFooterHTML({
      variant: styles.complianceFooterVariant,
      addressHtml,
      showAddress,
      showBadge,
      textColor: footerTextColor,
      fontFamily,
    });
  }


  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <title>Email</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  ${dynamicFontsLink || defaultGoogleFontsLink}
  <style type="text/css">
    /* Prevent horizontal scroll on mobile */
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      overflow-x: hidden !important;
      -webkit-text-size-adjust: 100%;
      -ms-text-size-adjust: 100%;
    }
    body, table, td, div {
      -webkit-text-size-adjust: 100%;
      -ms-text-size-adjust: 100%;
      box-sizing: border-box;
    }
    table {
      border-collapse: collapse;
      border-spacing: 0;
      mso-table-lspace: 0pt;
      mso-table-rspace: 0pt;
      width: 100%;
      max-width: 100%;
      table-layout: fixed;
    }
    td {
      word-wrap: break-word;
      overflow-wrap: break-word;
      max-width: 100%;
      box-sizing: border-box;
    }
    img {
      -ms-interpolation-mode: bicubic;
      border: 0;
      height: auto;
      line-height: 100%;
      outline: none;
      text-decoration: none;
      max-width: 100%;
      width: auto;
      display: block;
    }
    /* Word wrap for all text elements */
    h1, h2, h3, h4, h5, h6, p, div, td, span, a {
      word-wrap: break-word;
      overflow-wrap: break-word;
      max-width: 100%;
      box-sizing: border-box;
    }
    div {
      max-width: 100%;
      box-sizing: border-box;
    }
    .editorial-hero-overlay-btn-table {
      width: auto !important;
      max-width: 100% !important;
      table-layout: auto !important;
    }
    a.editorial-hero-overlay-button {
      width: auto !important;
      max-width: 100% !important;
      display: inline-block !important;
    }
    /* Mobile responsive */
    @media only screen and (max-width: 600px) {
      html, body {
        width: 100% !important;
        max-width: 100% !important;
        overflow-x: hidden !important;
      }
      * {
        max-width: 100% !important;
        box-sizing: border-box !important;
      }
      .email-container {
        width: 100% !important;
        max-width: 100% !important;
      }
      .email-content {
        width: 100% !important;
        max-width: 100% !important;
      }
      table {
        width: 100% !important;
        max-width: 100% !important;
        table-layout: fixed !important;
        min-width: 0 !important;
      }
      td {
        max-width: 100% !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
      }
      td[style*="width"] {
        max-width: 100% !important;
      }
      div {
        width: 100% !important;
        max-width: 100% !important;
        overflow-x: hidden !important;
      }
      img {
        max-width: 100% !important;
        height: auto !important;
      }
      a {
        max-width: 100% !important;
        word-wrap: break-word !important;
      }
      .editorial-hero-overlay-btn-table {
        width: auto !important;
        max-width: 100% !important;
        table-layout: auto !important;
        min-width: 0 !important;
      }
      a.editorial-hero-overlay-button {
        width: auto !important;
        max-width: 100% !important;
        display: inline-block !important;
        box-sizing: border-box !important;
      }
    }
    /* Tablet responsive (481px - 768px) */
    @media only screen and (min-width: 481px) and (max-width: 768px) {
      .email-container {
        width: 100% !important;
        max-width: 100% !important;
      }
      .email-content {
        width: 100% !important;
        max-width: 100% !important;
      }
      .columns-layout {
        display: block !important;
      }
      .columns-layout .column {
        width: 100% !important;
        display: block !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
      }
      img {
        max-width: 100% !important;
        height: auto !important;
      }
      .button-container {
        text-align: center !important;
      }
    }
    ${EDITORIAL_RESPONSIVE_CSS}
    ${HEADER_RESPONSIVE_CSS}
    ${MENU_RESPONSIVE_CSS}
    ${HERO_CONTENT_RESPONSIVE_CSS}
    ${FOOTER_EMAIL_BASE_CSS}
    ${FOOTER_RESPONSIVE_CSS}
    ${responsiveCss}
  </style>
  <!--[if mso]>
  <style type="text/css">
    table {border-collapse: collapse; border-spacing: 0; margin: 0;}
    div, td {padding: 0;}
    div {margin: 0 !important;}
  </style>
  <![endif]-->
</head>
<body style="margin: 0; padding: 0; background-color: ${backgroundColor}; font-family: ${fontFamily}, sans-serif; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;">
  ${previewTextHtml}
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin: 0; padding: 0; background-color: ${backgroundColor}; width: 100%; max-width: 100%; table-layout: fixed; box-sizing: border-box;">
    <tr>
      <td align="center" valign="top" style="padding: 0; width: 100%; max-width: 100%; box-sizing: border-box;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="${containerWidth}" class="email-container" style="max-width: ${containerMaxWidth}; background-color: ${contentBackgroundColor}; margin: 0 auto; width: ${containerWidth}; table-layout: fixed; box-sizing: border-box;">
          <tr>
            <td class="email-content" align="left" valign="top" style="padding: 0; width: 100%; max-width: 100%; box-sizing: border-box;">
              ${blockHTML}
            </td>
          </tr>
        </table>
        <!-- Relocated Mandatory Footer (Gutter Area) -->
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="${containerWidth}" class="email-container" style="max-width: ${containerMaxWidth}; margin: 0 auto; width: ${containerWidth}; table-layout: fixed;">
          <tr>
            <td style="padding: 32px 20px; text-align: center;">
              ${brandingHTML}
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

/**
 * Standardizes padding parsing across all block types.
 * Supports shorthands (1-4 values) and explicit overrides.
 */
function computePadding(styles: any, defaultPadding: string = '20px'): string {
  // Extract explicit side-specific padding if present
  let top = styles.paddingTop;
  let right = styles.paddingRight;
  let bottom = styles.paddingBottom;
  let left = styles.paddingLeft;

  // If shorthand is present, use it for any side not explicitly defined
  const shorthandRaw = typeof styles.padding === 'string' ? styles.padding.trim() : undefined;
  const shorthand =
    shorthandRaw && !isEffectivelyZeroPadding(shorthandRaw) ? shorthandRaw : undefined;
  if (shorthand) {
    const parts = shorthand.split(/\s+/);
    if (parts.length === 1) {
      if (!top) top = parts[0];
      if (!right) right = parts[0];
      if (!bottom) bottom = parts[0];
      if (!left) left = parts[0];
    } else if (parts.length === 2) {
      if (!top) top = parts[0];
      if (!right) right = parts[1];
      if (!bottom) bottom = parts[0];
      if (!left) left = parts[1];
    } else if (parts.length === 3) {
      if (!top) top = parts[0];
      if (!right) right = parts[1];
      if (!bottom) bottom = parts[2];
      if (!left) left = parts[1];
    } else if (parts.length === 4) {
      if (!top) top = parts[0];
      if (!right) right = parts[1];
      if (!bottom) bottom = parts[2];
      if (!left) left = parts[3];
    }
  }

  // Fallback to defaults for anything still missing
  top = top || defaultPadding;
  right = right || defaultPadding;
  bottom = bottom || defaultPadding;
  left = left || defaultPadding;

  return `${top} ${right} ${bottom} ${left}`;
}

function renderBlockToHTML(block: EmailBlockData, options: GenerateEmailHTMLOptions, globalFontFamily: string): string {
  const { type, content } = block;
  const isMobileExport = options.isMobile === true;
  const styles =
    isMobileExport && block.mobileStyles && Object.keys(block.mobileStyles).length > 0
      ? { ...block.styles, ...block.mobileStyles }
      : block.styles || {};
  const blockType = type === 'paragraph' ? 'text' : type;
  const fontFamily = formatFontFamily(styles.fontFamily, globalFontFamily);

  switch (blockType) {
    case 'caption':
    case 'preheader':
    case 'quote':
    case 'magic-text':
    case 'text': {
      const isHtmlContent = Boolean(content.isHtml);
      let rawText = (content.text || '').trim();

      if (isHtmlContent) {
        const baseStyle = `margin: 0; width: 100%; max-width: 100%; box-sizing: border-box; padding: ${styles.padding || '20px'}; font-size: ${styles.fontSize || '16px'}; font-weight: ${styles.fontWeight || '400'}; font-style: ${styles.fontStyle || 'normal'}; font-family: ${fontFamily}; line-height: ${styles.lineHeight || '160%'}; letter-spacing: ${styles.letterSpacing || '0px'}; color: ${styles.color || '#111827'}; text-align: ${styles.textAlign || 'left'}; text-decoration: ${styles.textDecoration || 'none'}; background-color: ${styles.backgroundColor || 'transparent'}; word-wrap: break-word; overflow-wrap: break-word;`;
        return `<div style="${baseStyle}">${rawText}</div>`;
      }

      // IMMEDIATELY trim all whitespace from the text - no exceptions
      const listType = content.listType as 'unordered' | 'ordered' | undefined;

      // All whitespace has been removed, so lengths are 0
      const leadingWhitespaceLength = 0;
      const trailingWhitespaceLength = 0;

      const hasLink =
        Boolean(content.linkUrl && content.linkUrl.trim()) &&
        typeof content.linkRangeStart === 'number' &&
        typeof content.linkRangeEnd === 'number' &&
        content.linkRangeEnd > content.linkRangeStart;
      const linkStart = typeof content.linkRangeStart === 'number' ? Math.max(0, content.linkRangeStart - leadingWhitespaceLength) : 0;
      const linkEnd = typeof content.linkRangeEnd === 'number'
        ? Math.min(rawText.length, Math.max(0, content.linkRangeEnd - leadingWhitespaceLength))
        : rawText.length;

      // Compute standardized padding for text blocks
      const textPadding = computePadding(styles, blockType === 'quote' ? '20px 40px' : '20px');

      // Adjust color ranges to account for trimmed leading and trailing whitespace
      const adjustedColorRanges: TextColorRange[] | undefined = content.colorRanges
        ? (content.colorRanges as TextColorRange[]).map((range) => ({
          start: Math.max(0, range.start - leadingWhitespaceLength),
          end: Math.min(rawText.length, Math.max(0, range.end - leadingWhitespaceLength)),
          color: range.color,
        })).filter((range) => range.end > range.start && range.start < rawText.length)
        : undefined;

      const colorRanges = normalizeColorRanges(
        adjustedColorRanges,
        rawText.length
      );

      const renderSegment = (start: number, end: number): string => {
        if (start >= end) return '';
        const boundaries = new Set<number>([start, end]);
        if (hasLink && linkEnd > linkStart) {
          const linkStartInRange = Math.max(start, Math.min(linkStart, end));
          const linkEndInRange = Math.max(linkStartInRange, Math.min(linkEnd, end));
          if (linkEndInRange > linkStartInRange) {
            boundaries.add(linkStartInRange);
            boundaries.add(linkEndInRange);
          }
        }
        colorRanges.forEach((range) => {
          const rangeStart = Math.max(start, Math.min(range.start, end));
          const rangeEnd = Math.max(rangeStart, Math.min(range.end, end));
          if (rangeEnd > rangeStart) {
            boundaries.add(rangeStart);
            boundaries.add(rangeEnd);
          }
        });
        const sorted = Array.from(boundaries).sort((a, b) => a - b);
        let html = '';
        for (let i = 0; i < sorted.length - 1; i += 1) {
          const segStart = sorted[i];
          const segEnd = sorted[i + 1];
          if (segEnd <= segStart) continue;
          const segmentText = rawText.slice(segStart, segEnd);
          if (!segmentText) continue;
          const appliedColor = colorRanges.find(
            (range) => segStart >= range.start && segStart < range.end
          )?.color;
          const isLinkSegment =
            hasLink &&
            segEnd > linkStart &&
            segStart < linkEnd;
          const escapedText = escapeHtml(segmentText);
          if (isLinkSegment) {
            const safeUrl = escapeHtml(content.linkUrl || '#');
            html += `<a href="${safeUrl}" target="${content.linkTarget || '_blank'}" style="color: ${appliedColor || styles.color || '#111827'}; text-decoration: ${styles.textDecoration || 'underline'};">${escapedText || escapeHtml(content.linkUrl || '')}</a>`;
          } else if (appliedColor) {
            html += `<span style="color: ${appliedColor};">${escapedText}</span>`;
          } else {
            html += escapedText;
          }
        }
        return html || '&nbsp;';
      };

      const baseStyle = `margin: 0; width: 100%; max-width: 100%; box-sizing: border-box; padding: ${textPadding}; font-size: ${styles.fontSize || '16px'}; font-weight: ${styles.fontWeight || '400'}; font-style: ${styles.fontStyle || 'normal'}; font-family: ${fontFamily}; line-height: ${styles.lineHeight || '160%'}; letter-spacing: ${styles.letterSpacing || '0px'}; color: ${styles.color || '#111827'}; text-align: ${styles.textAlign || 'left'}; text-decoration: ${styles.textDecoration || 'none'}; background-color: ${styles.backgroundColor || 'transparent'}; word-wrap: break-word; overflow-wrap: break-word;`;

      if (listType === 'unordered' || listType === 'ordered') {
        const lines = rawText.split('\n');
        let cursor = 0;
        const itemsHtml = lines
          .map((line: string) => {
            const start = cursor;
            const end = start + line.length;
            cursor = end + 1;
            return `<li style="margin-bottom: 0.35em;">${renderSegment(start, end)}</li>`;
          })
          .join('');
        const listTag = listType === 'ordered' ? 'ol' : 'ul';
        return `
        <div style="${baseStyle}">
          <${listTag} style="margin:0; padding-left: 24px; list-style-position: outside;">
            ${itemsHtml}
          </${listTag}>
        </div>
      `;
      }

      return `<div style="${baseStyle} white-space: pre-wrap; display: block;">${renderSegment(0, rawText.length)}</div>`;
    }

    case 'list': {
      const items = Array.isArray(content.items) ? content.items : (content.text ? content.text.split('\n') : ['List item 1', 'List item 2', 'List item 3']);
      const listType = content.listType === 'number' || content.listType === 'ordered' ? 'ordered' : (content.listType === 'check' ? 'check' : (content.listType === 'none' ? 'none' : 'unordered'));

      const listPadding = computePadding(styles, '20px');
      const textAlign = styles.textAlign || 'left';
      const isCentered = textAlign === 'center';
      const isRight = textAlign === 'right';

      const itemsHtml = items
        .filter((item: any) => typeof item === 'string' && item.trim())
        .map((item: string, index: number) => {
          const escapedItem = escapeHtml(item);
          let marker = '';
          const mColor = styles.markerColor || styles.color || '#374151';
          if (listType === 'check') {
            marker = `<span style="color: ${styles.markerColor || styles.color || '#000000'}; font-weight: bold;">✓</span>`;
          } else if (listType === 'ordered') {
            marker = `<span style="color: ${mColor};">${index + 1}.</span>`;
          } else if (listType !== 'none') {
            marker = `<span style="color: ${mColor};">•</span>`;
          }

          const tableAlign = isCentered ? 'center' : (isRight ? 'right' : 'left');

          if (listType === 'none') {
            return `<div style="margin-bottom: ${styles.listItemSpacing || '8px'}; text-align: ${textAlign}; font-family: ${fontFamily}; font-size: ${styles.fontSize || '16px'}; color: ${styles.color || '#374151'}; line-height: ${styles.lineHeight || '160%'};">${escapedItem}</div>`;
          }

          return `<table align="${tableAlign}" border="0" cellpadding="0" cellspacing="0" width="${isCentered ? 'auto' : '100%'}" style="margin: ${isCentered ? '0 auto ' + (styles.listItemSpacing || '8px') + ' auto' : (isRight ? '0 0 ' + (styles.listItemSpacing || '8px') + ' auto' : '0 auto ' + (styles.listItemSpacing || '8px') + ' 0')}; border-collapse: collapse; table-layout: fixed;"><tr style="font-family: ${fontFamily}; font-size: ${styles.fontSize || '16px'}; line-height: ${styles.lineHeight || '160%'};">${!isRight ? `<td style="padding-right: 12px; vertical-align: baseline; width: 30px; color: ${mColor}; text-align: right; white-space: nowrap;">${marker}</td>` : ''}<td style="text-align: ${textAlign}; color: ${styles.color || '#374151'}; word-wrap: break-word; overflow-wrap: break-word;">${escapedItem}</td>${isRight ? `<td style="padding-left: 12px; vertical-align: baseline; width: 30px; color: ${mColor}; text-align: left; white-space: nowrap;">${marker}</td>` : ''}</tr></table>`;
        })
        .join('');

      return `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: ${styles.backgroundColor || 'transparent'}; border-collapse: collapse;"><tr><td style="padding: ${listPadding};">${itemsHtml}</td></tr></table>`;
    }

    case 'title': {
      const titleText = (content.text || 'Display Title').trim();
      const titlePadding = computePadding(styles, '30px 20px 20px 20px');
      return `<h1 style="display: block; width: 100%; max-width: 100%; box-sizing: border-box; padding: ${titlePadding}; font-size: ${styles.fontSize || '48px'}; font-weight: ${styles.fontWeight || '800'}; color: ${styles.color || '#0F172A'}; text-align: ${styles.textAlign || 'left'}; letter-spacing: ${styles.letterSpacing || '-0.025em'}; line-height: ${styles.lineHeight || '110%'}; font-family: ${fontFamily}; margin: 0; background-color: ${styles.backgroundColor || 'transparent'}; word-wrap: break-word; overflow-wrap: break-word; white-space: pre-wrap;">${escapeHtml(titleText)}</h1>`;
    }

    case 'subtitle': {
      const subtitleText = (content.text || 'Supporting subtitle text').trim();
      const subtitlePadding = computePadding(styles, '10px 20px 20px 20px');
      return `<h2 style="display: block; width: 100%; max-width: 100%; box-sizing: border-box; padding: ${subtitlePadding}; font-size: ${styles.fontSize || '20px'}; font-weight: ${styles.fontWeight || '500'}; color: ${styles.color || '#64748B'}; text-align: ${styles.textAlign || 'left'}; line-height: ${styles.lineHeight || '150%'}; font-family: ${fontFamily}; margin: 0; background-color: ${styles.backgroundColor || 'transparent'}; word-wrap: break-word; overflow-wrap: break-word; white-space: pre-wrap;">${escapeHtml(subtitleText)}</h2>`;
    }

    case 'heading': {
      const headingTag = `h${content.level || 1}`;
      const headingPadding = computePadding(styles, '20px');
      const headingFontStyle = styles.fontStyle || 'normal';
      const headingDecoration = styles.textDecoration || 'none';
      const headingPosition = styles.headingPosition;
      const headingText = (content.text || 'Heading').trim();
      const baseText = escapeHtml(headingText);
      const scaleFontSize = (value: string | number | undefined, factor: number) => {
        if (typeof value === 'number') {
          return `${value * factor}px`;
        }
        if (typeof value === 'string') {
          const parsed = parseFloat(value);
          if (!Number.isNaN(parsed)) {
            const unit = value.replace(parsed.toString(), '') || 'px';
            return `${parsed * factor}${unit}`;
          }
        }
        return undefined;
      };
      const supFontSize = scaleFontSize(styles.fontSize, 0.75) || '0.75em';
      const subFontSize = scaleFontSize(styles.fontSize, 0.8) || '0.8em';
      const sanitizeRange = (text: string, start?: number, end?: number) => {
        const length = text.length;
        const rawStart = typeof start === 'number' ? start : 0;
        const rawEnd = typeof end === 'number' ? end : length;
        const safeStart = Math.max(0, Math.min(rawStart, length));
        const safeEnd = Math.max(safeStart, Math.min(rawEnd, length));
        return { start: safeStart, end: safeEnd };
      };
      const ensureUnderline = (decoration?: string) => {
        if (!decoration) return 'underline';
        const tokens = decoration
          .split(/\s+/)
          .map((token) => token.trim())
          .filter(Boolean);
        if (!tokens.includes('underline')) {
          tokens.push('underline');
        }
        return tokens.join(' ');
      };
      const { start: linkStart, end: linkEnd } = sanitizeRange(
        headingText,
        content.linkRangeStart,
        content.linkRangeEnd
      );
      const rawLinkUrl = (content.linkUrl || '').trim();
      const hasLinkedText = rawLinkUrl && linkEnd > linkStart;
      const safeLinkUrl = escapeHtml(rawLinkUrl);
      const linkedTextDecoration = ensureUnderline(headingDecoration);

      const colorRanges = normalizeColorRanges(
        content.colorRanges as TextColorRange[] | undefined,
        headingText.length
      );

      const renderHeadingContent = (): string => {
        if (!headingText.length) return '';
        const boundaries = new Set<number>([0, headingText.length]);
        if (hasLinkedText && linkEnd > linkStart) {
          boundaries.add(linkStart);
          boundaries.add(linkEnd);
        }
        colorRanges.forEach((range) => {
          const start = Math.max(0, Math.min(range.start, headingText.length));
          const end = Math.max(start, Math.min(range.end, headingText.length));
          if (end > start) {
            boundaries.add(start);
            boundaries.add(end);
          }
        });
        const sorted = Array.from(boundaries).sort((a, b) => a - b);
        let html = '';
        for (let i = 0; i < sorted.length - 1; i += 1) {
          const segStart = sorted[i];
          const segEnd = sorted[i + 1];
          if (segEnd <= segStart) continue;
          const segmentText = headingText.slice(segStart, segEnd);
          if (!segmentText) continue;
          const colorValue = colorRanges.find(
            (range) => segStart >= range.start && segStart < range.end
          )?.color;
          const isLinkSegment =
            hasLinkedText &&
            segEnd > linkStart &&
            segStart < linkEnd;
          const escapedText = escapeHtml(segmentText);
          if (isLinkSegment) {
            html += `<a href="${safeLinkUrl}" target="${content.linkTarget || '_blank'}" rel="noopener noreferrer" style="color: ${colorValue || styles.color || '#111827'}; text-decoration: ${linkedTextDecoration}; display: inline-block;">${escapedText || safeLinkUrl}</a>`;
          } else if (colorValue) {
            html += `<span style="color: ${colorValue};">${escapedText}</span>`;
          } else {
            html += escapedText;
          }
        }
        return html || baseText;
      };

      const baseContentHtml = renderHeadingContent();
      const positionedText =
        headingPosition === 'superscript'
          ? `<sup style="font-size:${supFontSize}; line-height:1; vertical-align: super;">${baseContentHtml}</sup>`
          : headingPosition === 'subscript'
            ? `<sub style="font-size:${subFontSize}; line-height:1; vertical-align: sub;">${baseContentHtml}</sub>`
            : baseContentHtml;
      const headingInnerHtml = positionedText;

      return `<${headingTag} style="display: block; width: 100%; max-width: 100%; box-sizing: border-box; padding: ${headingPadding}; font-size: ${styles.fontSize || '32px'}; font-weight: ${styles.fontWeight || '700'}; font-style: ${headingFontStyle}; color: ${styles.color || '#111827'}; text-align: ${styles.textAlign || 'left'}; line-height: ${styles.lineHeight || '140%'}; letter-spacing: ${styles.letterSpacing || '0px'}; font-family: ${fontFamily}; text-decoration: ${headingDecoration}; margin: 0; background-color: ${styles.backgroundColor || 'transparent'}; word-wrap: break-word; overflow-wrap: break-word; white-space: pre-wrap;">${headingInnerHtml}</${headingTag}>`;
    }

    case 'image': {
      const imageSrc = content.src || '';
      const hasImage = Boolean(imageSrc && imageSrc.trim() !== '');
      const isAutoWidth = content.autoWidth !== false;
      const imageShape = content.imageShape || 'square';
      const placeholderHeight = styles.minHeight || content.height || '400px';
      const minHeight = hasImage
        ? content.height && !isAutoWidth
          ? content.height
          : 'auto'
        : placeholderHeight;

      // Parse container width
      const isMobile = options.isMobile === true;
      const baseContainerWidth = parseInt(styles.containerWidth?.replace('px', '') || '600', 10);
      const containerWidthPx = isMobile ? Math.min(baseContainerWidth, 480) : baseContainerWidth;

      // Parse width - can be percentage (e.g., "50%") or pixels
      const imageWidthStr = content.width || '100%';
      let widthValue = 100;
      let widthUnit = '%';

      const widthMatch = imageWidthStr.match(/^(\d+(?:\.\d+)?)(%|px)?$/);
      if (widthMatch) {
        widthValue = parseFloat(widthMatch[1]);
        widthUnit = widthMatch[2] || '%';
      }

      const isManualPercentage = !isAutoWidth && widthUnit === '%';
      const calculatedWidthPx = isManualPercentage
        ? Math.round((containerWidthPx * widthValue) / 100)
        : widthUnit === 'px' ? widthValue : containerWidthPx;

      const imageWidthStyle = isAutoWidth ? 'auto' : imageWidthStr;

      // For email attributes, try to use pixels for better compatibility
      const widthAttrValue = isAutoWidth
        ? (content.metadata?.width && content.metadata.width < containerWidthPx ? content.metadata.width : containerWidthPx)
        : calculatedWidthPx;
      const widthAttr = ` width="${widthAttrValue}"`;

      // Handle height
      const contentHeight = content.height || 'auto';
      const isAutoHeight = contentHeight === 'auto';

      let finalHeightStyle = contentHeight;

      // Height attribute for Outlook
      let heightAttr = '';
      if (!isAutoHeight) {
        const hMatch = contentHeight.match(/^(\d+(?:\.\d+)?)(px)?$/);
        if (hMatch) {
          heightAttr = ` height="${hMatch[1]}"`;
        }
      }

      // Background color for the image container
      const bgColor = styles.backgroundColor || 'transparent';

      const borderRadius =
        imageShape === 'circle' ? '50%' :
          imageShape === 'arch' ? '100px 100px 0 0' :
            imageShape === 'shield' ? '0 0 100px 100px' :
              imageShape === 'rounded' ? '24px' :
                imageShape === 'leaf' ? '50px 0 50px 0' :
                  imageShape === 'blob' ? '60% 40% 30% 70% / 60% 30% 70% 40%' :
                    imageShape === 'badge' ? '24px 24px 24px 0' :
                      imageShape === 'pill' ? '100px' : '0';

      // clip-path is not supported in email clients — use border-radius fallback for all shapes
      const clipPath = ''; // intentionally empty: clip-path unsupported in email

      // object-fit is not supported in Outlook — omitted from email HTML

      const imageHtml = hasImage
        ? `<img src="${imageSrc}" alt="${escapeHtml(content.alt || 'Image')}"${widthAttr}${heightAttr} style="width: ${imageWidthStyle}; height: ${finalHeightStyle}; min-height: ${minHeight}; max-width: 100%; display: block; ${(styles.textAlign || 'center') === 'center' ? 'margin-left: auto; margin-right: auto;' : (styles.textAlign === 'right' ? 'margin-left: auto; margin-right: 0;' : 'margin-left: 0; margin-right: auto;')} ${borderRadius !== "0" ? `border-radius: ${borderRadius};` : ""} ${clipPath}" />`
        : `
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="width: 100%;"><tr><td align="center" valign="middle" height="${placeholderHeight}" style="min-height: ${placeholderHeight}; background-color: #F3F4F6; border: 2px dashed #D1D5DB; color: #6B7280; border-radius: ${borderRadius}; text-align: center;">
          <div style="font-size: 14px; font-weight: 500; font-family: Arial, sans-serif;">Image Placeholder</div>
        </td></tr></table>
      `;

      const resolvedLinkType = resolveImageLinkType(content);
      const actionHref = resolvedLinkType ? buildActionHref(resolvedLinkType, content.linkUrl) : null;

      const wrappedImage =
        actionHref
          ? `<a href="${escapeHtml(actionHref)}" target="${escapeHtml(content.linkTarget || '_blank')}" rel="${content.linkTarget === '_blank' ? 'noopener noreferrer' : ''
          }" style="display: block; text-decoration: none;">${imageHtml}</a>`
          : imageHtml;

      // Sticker overlay for email
      const overlayStickerData = content.overlaySticker;
      const hasOverlaySticker = overlayStickerData?.src;
      const overlayTextData = content.overlayText || {};
      const hasOverlayText =
        Boolean(overlayTextData.enabled) &&
        (Boolean((overlayTextData.headline || '').trim()) || Boolean((overlayTextData.subtext || '').trim()));

      let overlayHtml = '';
      let overlayTextHtml = '';
      if (hasOverlaySticker) {
        const stickerSize = overlayStickerData.size || 25;
        const pos = overlayStickerData.position || 'top-right';
        const isSvg = overlayStickerData.stickerType === 'builtin' && overlayStickerData.src.startsWith('<svg');
        const stickerImgSrc = isSvg
          ? `data:image/svg+xml,${encodeURIComponent(overlayStickerData.src).replace(/'/g, '%27').replace(/"/g, '%22')}`
          : escapeHtml(overlayStickerData.src);

        // Position mapping
        const offset = '6%';
        let posStyle = '';
        switch (pos) {
          case 'top-left': posStyle = `top: ${offset}; left: ${offset};`; break;
          case 'top-center': posStyle = `top: ${offset}; left: 50%; transform: translateX(-50%);`; break;
          case 'top-right': posStyle = `top: ${offset}; right: ${offset};`; break;
          case 'center-left': posStyle = `top: 50%; left: ${offset}; transform: translateY(-50%);`; break;
          case 'center': posStyle = `top: 50%; left: 50%; transform: translate(-50%, -50%);`; break;
          case 'center-right': posStyle = `top: 50%; right: ${offset}; transform: translateY(-50%);`; break;
          case 'bottom-left': posStyle = `bottom: ${offset}; left: ${offset};`; break;
          case 'bottom-center': posStyle = `bottom: ${offset}; left: 50%; transform: translateX(-50%);`; break;
          case 'bottom-right': posStyle = `bottom: ${offset}; right: ${offset};`; break;
          default: posStyle = `top: ${offset}; right: ${offset};`; break;
        }
        const rotation = overlayStickerData.rotation || 0;
        // Append rotation to existing transforms if needed
        if (rotation) {
          // If posStyle already has transform, append rotate
          if (posStyle.includes('transform:')) {
            posStyle = posStyle.replace(/transform:\s*([^;]+);/, (_, t) => `transform: ${t} rotate(${rotation}deg);`);
          } else {
            posStyle += ` transform: rotate(${rotation}deg);`;
          }
        }

        overlayHtml = `<img src="${stickerImgSrc}" alt="${escapeHtml(overlayStickerData.stickerName || 'Sticker')}" style="position: absolute; ${posStyle} width: ${stickerSize}%; max-width: 150px; height: auto; z-index: 10; pointer-events: none;" />`;
      }

      if (hasOverlayText) {
        const headline = escapeHtml((overlayTextData.headline || '').trim()).replace(/\n/g, '<br/>');
        const subtext = escapeHtml((overlayTextData.subtext || '').trim()).replace(/\n/g, '<br/>');
        const position = overlayTextData.position || 'center';
        const align = overlayTextData.align || 'center';
        const overlayFontFamily = formatFontFamily(overlayTextData.fontFamily, fontFamily);
        const textShadow =
          overlayTextData.textShadow === false
            ? 'none'
            : '0 2px 8px rgba(0,0,0,0.35)';
        let posStyle = 'left: 8%; right: 8%;';
        if (position === 'top') posStyle += ' top: 10%;';
        else if (position === 'bottom') posStyle += ' bottom: 10%;';
        else posStyle += ' top: 50%; transform: translateY(-50%);';

        overlayTextHtml = `
          <div style="position: absolute; z-index: 12; pointer-events: none; ${posStyle}">
            ${headline
              ? `<div style="font-size: ${overlayTextData.headlineSize || 56}px; font-weight: ${overlayTextData.headlineWeight || 800}; font-family: ${overlayFontFamily}; color: ${overlayTextData.headlineColor || '#D9FF4A'}; line-height: 1.05; letter-spacing: ${overlayTextData.headlineLetterSpacing || '0px'}; text-align: ${align}; text-shadow: ${textShadow}; margin-bottom: ${subtext ? '14px' : '0'};">${headline}</div>`
              : ''}
            ${subtext
              ? `<div style="font-size: ${overlayTextData.subtextSize || 18}px; font-weight: ${overlayTextData.subtextWeight || 500}; font-family: ${overlayFontFamily}; color: ${overlayTextData.subtextColor || '#FFFFFF'}; line-height: 1.45; letter-spacing: ${overlayTextData.subtextLetterSpacing || '0px'}; text-align: ${align}; text-shadow: ${textShadow};">${subtext}</div>`
              : ''}
          </div>
        `;
      }

      const imagePadding = computePadding(styles, '20px');

      if (hasOverlaySticker || hasOverlayText) {
        return `
          <div style="padding: ${imagePadding}; text-align: ${styles.textAlign || 'center'}; min-height: ${minHeight}; background-color: ${bgColor};">
            <div style="position: relative; display: inline-block; max-width: 100%;">
              ${wrappedImage}
              ${overlayTextHtml}
              ${overlayHtml}
            </div>
          </td></tr></table>
        `;
      }

      return `
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%"><tr><td align="${styles.textAlign || 'center'}" style="padding: ${imagePadding}; text-align: ${styles.textAlign || 'center'}; background-color: ${bgColor};">
          ${wrappedImage}
        </td></tr></table>
      `;
    }

    case 'button': {
      const isMobile = options.isMobile === true;
      const actionType = (content.actionType as 'open-url' | 'download-file') || 'open-url';
      const isDownloadAction = actionType === 'download-file';
      const buttonHref = isDownloadAction
        ? (content.downloadFileUrl && String(content.downloadFileUrl).trim()) || '#'
        : buildActionHref(content.actionType, content.url) || '#';
      return renderButtonBlockHtml(
        {
          ...content,
          url: buttonHref,
        },
        styles,
        { isMobile, fontFamily }
      );
    }

    case 'divider':
      return `
        <div style="padding: ${styles.padding || '20px'}; background-color: ${styles.backgroundColor || 'transparent'};">
          <hr style="border: none; border-top: ${styles.borderWidth || '1px'} ${styles.borderStyle || 'solid'} ${styles.borderColor || '#E5E7EB'}; margin: 0;" />
        </div>
      `;

    case 'spacer':
      return `
        <div style="height: ${content.height || '40px'}; padding: ${styles.padding || '0px'}; background-color: ${styles.backgroundColor || 'transparent'};"></div>
      `;

    case 'logo': {
      const logoAlign = styles.textAlign || 'center';
      const isMobile = options.isMobile === true;
      const logoPadding = styles.paddingTop || styles.paddingBottom || styles.paddingLeft || styles.paddingRight
        ? `${styles.paddingTop || '0px'} ${styles.paddingRight || '0px'} ${styles.paddingBottom || '0px'} ${styles.paddingLeft || '0px'}`
        : (styles.padding || '20px');
      const logoActionHref = buildActionHref(content.linkType, content.linkUrl);
      // Use responsive sizing unless user has explicitly set a custom width (not the old default '200px')
      const logoWidth = (content.width && content.width !== '200px') ? content.width : (isMobile ? '120px' : '150px');
      const logoInner = content.src
        ? `<img src="${content.src}" alt="${escapeHtml(content.alt || 'Logo')}" style="width: ${logoWidth} !important; height: auto !important; max-width: 100% !important; display: inline-block; box-sizing: border-box;" />`
        : `<div style="display: inline-block; width: ${logoWidth}; padding: 12px 16px; border: 1px solid #E2E8F0; border-radius: 8px; background-color: #F8FAFC; color: #475569; font-family: ${fontFamily}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; text-align: center; box-sizing: border-box;">Brand Logo</div>`;
      const wrappedLogoHtml = logoActionHref
        ? `<a href="${escapeHtml(logoActionHref)}" target="${escapeHtml(content.linkTarget || '_blank')}" rel="${content.linkTarget === '_blank' ? 'noopener noreferrer' : ''
        }" style="display: inline-block; text-decoration: none; max-width: 100%; box-sizing: border-box;">${logoInner}</a>`
        : logoInner;
      return `
        <div style="padding: ${logoPadding}; text-align: ${logoAlign}; background-color: ${styles.backgroundColor || 'transparent'}; width: 100%; max-width: 100%; box-sizing: border-box; overflow: hidden;">
          ${wrappedLogoHtml}
        </div>
      `;
    }

    case 'video': {
      const rawVideoUrl = (content.videoUrl || '').trim();
      const enableInteractiveVideo = options.enableInteractiveVideo;
      const embedData = enableInteractiveVideo ? getVideoEmbedData(rawVideoUrl) : null;
      const derivedThumbnail = resolveVideoThumbnailUrl(content);
      const playButtonStyle = content.playButtonStyle || 'circle';

      const renderInteractiveVideo = (): string => {
        if (!embedData) return '';
        if (embedData.type === 'file') {
          return `
            <video controls poster="${derivedThumbnail}" style="width: 100%; max-width: 600px; border-radius: 8px; background-color: #000;" preload="metadata">
              <source src="${embedData.src}" />
              Your browser does not support the video tag.
            </video>
          `;
        }
        return `
          <div style="position: relative; width: 100%; max-width: 600px; margin: 0 auto; border-radius: 8px; overflow: hidden; background-color: #000; padding-bottom: 56.25%; height: 0;">
            <iframe
              src="${embedData.src}"
              title="${embedData.title}"
              style="border: 0; position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
          </div>
        `;
      };

      const renderThumbnailPreview = (): string => {
        if (!rawVideoUrl || !derivedThumbnail || derivedThumbnail.includes('via.placeholder.com')) {
          // Email-safe placeholder: table for height, no aspect-ratio
          return `
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="width: 100%; max-width: 600px; margin: 0 auto; border-radius: 12px; background-color: #F8FAFC; border: 1px solid #CBD5E1;">
              <tr>
                <td align="center" valign="middle" height="200" style="padding: 40px 24px; text-align: center; background-color: #F8FAFC; border-radius: 12px;">
                  <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center" style="margin: 0 auto 16px auto;">
                    <tr>
                      <td align="center" valign="middle" width="64" height="64" style="width: 64px; height: 64px; background-color: #FFFFFF; border-radius: 50%; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);">
                        <span style="font-size: 24px; color: #6366F1; line-height: 1;">&#9658;</span>
                      </td>
                    </tr>
                  </table>
                  <div style="font-family: ${fontFamily}; font-size: 16px; font-weight: 700; color: #1E293B; margin-bottom: 4px;">Video Placeholder</div>
                  <div style="font-family: ${fontFamily}; font-size: 13px; color: #64748B;">Add a YouTube or Vimeo URL to show a video preview</div>
                </td>
              </tr>
            </table>
          `;
        }

        return `
          <div style="position: relative; width: 100%; max-width: 600px; margin: 0 auto; background-color: #000; border-radius: 8px; overflow: hidden;">
            <img src="${derivedThumbnail}" alt="Video thumbnail" style="width: 100%; display: block;" />
            ${videoPlayButtonOverlayHtml(playButtonStyle)}
          </div>
        `;
      };

      const videoInnerHtml = embedData ? renderInteractiveVideo() : renderThumbnailPreview();
      const linkTarget = !embedData ? (content.actionUrl || rawVideoUrl) : '';
      const wrappedVideo =
        linkTarget && linkTarget.trim() !== ''
          ? `<a href="${escapeHtml(linkTarget)}" target="${content.actionTarget || '_blank'}" rel="noopener noreferrer" style="text-decoration:none;display:inline-block;">${videoInnerHtml}</a>`
          : videoInnerHtml;

      return `
        <div style="padding: ${computePadding(styles, '20px')}; text-align: ${styles.textAlign || 'center'}; background-color: ${styles.backgroundColor || 'transparent'};">
          ${wrappedVideo}
        </div>
      `;
    }

    case 'accordion': {
      const items = (content.items as any[]) || [];
      return `
        <div style="padding: ${styles.padding || '20px'}; background-color: ${styles.backgroundColor || 'transparent'}; text-align: ${styles.textAlign || 'left'}; font-family: ${fontFamily};">
          ${items.map((item) => `
            <div style="margin-bottom: 12px; border: 1px solid #E5E7EB; border-radius: 6px; overflow: hidden;">
              <div style="padding: 12px 16px; background-color: #F9FAFB; font-weight: 600; font-size: 14px; color: #111827; border-bottom: 1px solid #E5E7EB; text-align: ${styles.textAlign || 'left'}; font-family: ${fontFamily};">
                ${escapeHtml(item.title || 'Title')}
              </div>
              <div style="padding: 12px 16px; background-color: #FFFFFF; font-size: 14px; color: #4B5563; line-height: 1.5; text-align: ${styles.textAlign || 'left'}; font-family: ${fontFamily};">
                ${escapeHtml(item.content || 'Content')}
              </div>
            </div>
          `).join('')}
        </div>
      `;
    }

    case 'image-carousel': {
      const images = (content.images as any[]) || [];
      const carouselAlign = styles.textAlign || 'center';
      const carouselTableMargin =
        carouselAlign === 'left' ? 'margin: 0 auto 0 0;' : carouselAlign === 'right' ? 'margin: 0 0 0 auto;' : 'margin: 0 auto;';
      // Email-safe: no overflow-x scroll (unsupported), use wrapping rows instead
      const imagesPerRow = 2;
      const rows: typeof images[] = [];
      for (let i = 0; i < images.length; i += imagesPerRow) {
        rows.push(images.slice(i, i + imagesPerRow));
      }
      return `
        <div style="padding: ${computePadding(styles, '20px')}; text-align: ${carouselAlign}; background-color: ${styles.backgroundColor || 'transparent'};">
          <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="${carouselTableMargin} width: 100%;">
            ${rows.map((row) => `
            <tr>
              ${row.map((img, index) => `
                <td style="padding: ${index === 0 ? '0 5px 10px 0' : '0 0 10px 5px'}; vertical-align: top; width: 50%;">
                  <div style="border-radius: 8px; overflow: hidden; border: 1px solid #E5E7EB; background-color: #F3F4F6;">
                    ${img.src
          ? `<img src="${escapeHtml(img.src)}" alt="${escapeHtml(img.alt || 'Image')}" width="100%" style="width: 100%; height: auto; display: block; border: 0;" />`
          : `<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%"><tr><td align="center" valign="middle" height="150" style="color: #9CA3AF; font-size: 12px; font-family: Arial, sans-serif; background-color: #F3F4F6;">No Image</td></tr></table>`
        }
                  </div>
                </td>
              `).join('')}
              ${row.length < imagesPerRow ? `<td style="width: 50%;"></td>` : ''}
            </tr>`).join('')}
          </table>
        </div>
      `;
    }

    case 'sticker': {
      const stickerSrc = content.src || '';
      const stickerWidth = content.width || '120px';
      const stickerAlt = escapeHtml(content.alt || content.stickerName || 'Sticker');
      const isSvgSticker = isSvgStickerContent(content.stickerType, stickerSrc);

      if (!stickerSrc) {
        return `
        <div style="padding: ${computePadding(styles, '10px')}; text-align: ${styles.textAlign || 'center'}; background-color: ${styles.backgroundColor || 'transparent'};">
          <div style="display: inline-block; width: 160px; padding: 32px 16px; background-color: #F8FAFC; border-radius: 16px; border: 2px dashed #CBD5E1; color: #94A3B8; font-family: ${fontFamily}; font-size: 13px; font-weight: 500; text-align: center;">
            Choose a sticker
          </div>
        </div>`;
      }

      // For SVG stickers, convert to data URI for email compatibility
      const imgSrc = isSvgSticker
        ? `data:image/svg+xml,${encodeURIComponent(stickerSrc).replace(/'/g, '%27').replace(/"/g, '%22')}`
        : escapeHtml(stickerSrc);

      return `
        <div style="padding: ${computePadding(styles, '10px')}; text-align: ${styles.textAlign || 'center'}; background-color: ${styles.backgroundColor || 'transparent'};">
          <img src="${imgSrc}" alt="${stickerAlt}" style="width: ${stickerWidth}; max-width: 100%; height: auto; display: inline-block;" />
        </div>
      `;
    }

    case 'giphy': {
      const width = content.width || '100%';
      return `
        <div style="padding: ${computePadding(styles, '20px')}; text-align: ${styles.textAlign || 'center'}; background-color: ${styles.backgroundColor || 'transparent'};">
          <img src="${content.src}" alt="${escapeHtml(content.alt || 'GIF')}" style="width: ${width}; max-width: 100%; height: auto; display: inline-block;" />
        </div>
      `;
    }

    case 'menu':
      return renderMenuBlockHtml(content, styles, fontFamily, options.isMobile === true);



    case 'social': {
      const platforms = content.platforms || [];
      const isMobile = options.isMobile === true;
      const defaults = {
        display: 'icon-only',
        iconStyle: 'solid',
        layout: 'horizontal',
        iconColor: '#000000',
        alignment: 'center',
        gap: isMobile ? 8 : 12,
        size: isMobile ? 20 : 24,
      };
      const iconSettings = { ...defaults, ...(content.iconSettings || {}) };

      const size = iconSettings.size || 24;
      const gap = iconSettings.gap || 12;
      const isVertical = iconSettings.layout === 'vertical';
      const alignment = iconSettings.alignment || styles.textAlign || 'center';

      const renderIconHtml = (platform: any) => {
        const platformData = getPlatformData(platform.icon || 'facebook');
        if (!platformData) return '';

        const brandColor = platformData.color || '#64748B';
        const effectiveStyle = platform.style || iconSettings.iconStyle || 'solid';

        const isOutline = effectiveStyle.includes('outline');
        const isBrand = effectiveStyle.includes('brand');
        const isCircle = effectiveStyle.includes('circle');
        const isSquare = effectiveStyle.includes('square');
        const isShape = isCircle || isSquare;

        const iconColor = isBrand ? brandColor : (iconSettings.iconColor || '#000000');
        const containerSize = isShape ? Math.round(size * 1.6) : size;

        const path = platformData.path;
        const viewBox = platformData.viewBox || '0 0 24 24';

        if (isShape) {
          const innerIconColor = isOutline ? iconColor : (platform.icon === 'snapchat' ? '#000000' : '#FFFFFF');
          const borderRadius = isCircle ? '50%' : (isSquare ? '4px' : '0');
          const bgColor = isOutline ? 'transparent' : iconColor;
          const borderStr = isOutline ? `border: 2px solid ${iconColor};` : 'border: none;';

          return `
            <div style="display: table; width: ${containerSize}px; height: ${containerSize}px; background-color: ${bgColor}; border-radius: ${borderRadius}; ${borderStr} border-collapse: separate;">
              <div style="display: table-cell; vertical-align: middle; text-align: center;">
                <svg viewBox="${viewBox}" width="${size}" height="${size}" fill="${isOutline ? 'none' : innerIconColor}" stroke="${isOutline ? innerIconColor : 'none'}" stroke-width="${isOutline ? 2 : 0}" style="display: inline-block; vertical-align: middle; width: ${size}px; height: ${size}px;">
                  <path d="${path}" />
                </svg>
              </div>
            </div>
          `;
        }

        return `
          <svg viewBox="${viewBox}" width="${size}" height="${size}" fill="${isOutline ? 'none' : iconColor}" stroke="${isOutline ? iconColor : 'none'}" stroke-width="${isOutline ? 2 : 0}" style="display: inline-block; vertical-align: middle; width: ${size}px; height: ${size}px;">
            <path d="${path}" />
          </svg>
        `;
      };

      const socialLinksHtml = platforms.map((platform: any, index: number) => {
        const platformName = escapeHtml(platform.name || platform.icon || 'Social');
        const iconHtml = renderIconHtml(platform);
        const href = platform.url || '#';

        const linkHtml = `
          <a href="${href}" target="_blank" title="${platformName}" style="text-decoration: none; color: ${iconSettings.iconColor}; font-family: ${fontFamily}; font-size: 14px; line-height: 1;">
            <table role="presentation" border="0" cellspacing="0" cellpadding="0" style="width: auto; border-collapse: collapse;">
              <tr>
                <td valign="middle" style="line-height: 1;">
                  ${iconHtml}
                </td>
                ${iconSettings.display === 'icon-text' ? `
                  <td style="padding-left: 8px; font-family: ${fontFamily}; font-size: 14px; font-weight: 500; color: ${iconSettings.iconColor}; line-height: 1; vertical-align: middle;">
                    ${platformName}
                  </td>
                ` : ''}
              </tr>
            </table>
          </a>
        `;

        // Wrap in TD for layout - Important: do NOT use align="${alignment}" here as it causes spacing issues
        if (isVertical) {
          const paddingBottom = index === platforms.length - 1 ? '0' : `${gap}px`;
          return `<tr><td style="padding-bottom: ${paddingBottom};">${linkHtml}</td></tr>`;
        } else {
          const paddingRight = index === platforms.length - 1 ? '0' : `${gap}px`;
          return `<td style="padding-right: ${paddingRight}; font-size: 0; line-height: 0;">${linkHtml}</td>`;
        }
      }).join('');

      const tableContent = isVertical ? socialLinksHtml : `<tr>${socialLinksHtml}</tr>`;

      let tableAlign: 'left' | 'center' | 'right' = 'center';
      if (alignment === 'left') tableAlign = 'left';
      else if (alignment === 'right') tableAlign = 'right';

      // Bulletproof email centering uses a 100% width table with an aligned TD
      return `
        <div style="padding: ${styles.padding || '20px'}; background-color: ${styles.backgroundColor || 'transparent'};">
          <table role="presentation" border="0" cellspacing="0" cellpadding="0" width="100%">
            <tr>
              <td align="${tableAlign}" style="font-size: 0; line-height: 0;">
                <table role="presentation" border="0" cellspacing="0" cellpadding="0" align="${tableAlign}" style="margin: 0 ${tableAlign === 'center' ? 'auto' : (tableAlign === 'left' ? '0' : '0 0 0 auto')}; width: auto;">
                  ${tableContent}
                </table>
              </td>
            </tr>
          </table>
        </div>
      `;
    }

    case 'creative-assistant':
      return `
        <div style="padding: ${styles.padding || '20px'}; background-color: ${styles.backgroundColor || '#F0F9FF'}; border: ${styles.border || '2px dashed #3B82F6'}; border-radius: 8px; text-align: center;">
          <div style="font-size: 24px; margin-bottom: 8px;">✨</div>
          <div style="font-weight: 600; margin-bottom: 4px;">Creative Assistant</div>
          <div style="font-size: 14px; color: #6B7280;">
            ${escapeHtml(content.prompt || 'Generate creative content')}
          </div>
        </div>
      `;

    case 'survey': {
      const surveyQuestions = (content.questions as any[] | undefined)?.map((question: any) => {
        const options: any[] = Array.isArray(question.options) ? question.options : [];
        const optionCells = options.length > 0
          ? options.map((opt: any) => {
              const label = typeof opt === 'string' ? opt : (opt.text || opt.label || 'Option');
              return `<td style="padding: 0 6px 8px 0; vertical-align: top;">
                <div style="padding: 8px 14px; border: 1px solid #E5E7EB; border-radius: 4px; background-color: #FFFFFF; font-family: ${fontFamily}; font-size: 14px; color: #374151; white-space: nowrap;">${escapeHtml(label)}</div>
              </td>`;
            }).join('')
          : `<td style="padding: 0 0 8px 0;">
              <div style="padding: 8px 14px; border: 1px solid #E5E7EB; border-radius: 4px; background-color: #FFFFFF; font-family: ${fontFamily}; font-size: 14px; color: #9CA3AF;">No options added yet</div>
            </td>`;
        return `
          <div style="margin-bottom: 16px;">
            <div style="margin-bottom: 8px; font-family: ${fontFamily}; font-size: 15px; font-weight: 500; color: #111827;">${escapeHtml(question.text || 'Question')}</div>
            <table role="presentation" cellspacing="0" cellpadding="0" border="0"><tr>${optionCells}</tr></table>
          </div>
        `;
      }).join('') || '';
      return `
        <div style="padding: ${styles.padding || '20px'}; background-color: ${styles.backgroundColor || '#F8FAFC'}; border-radius: 8px;">
          ${content.title ? `<div style="font-weight: 600; font-size: 16px; color: #111827; margin-bottom: 14px; font-family: ${fontFamily};">${escapeHtml(content.title)}</div>` : ''}
          ${surveyQuestions || `<div style="color: #9CA3AF; font-size: 14px; font-family: ${fontFamily};">Add questions in the properties panel</div>`}
        </div>
      `;
    }

    case 'countdown': {
      // Email clients cannot run JavaScript — render a static snapshot showing the target date/time text
      const targetDate = content.targetDate || content.endDate;
      const title = content.title || 'Limited Time Offer';
      const expiredMessage = content.expiredMessage || 'This offer has ended';
      const showLabels = content.showLabels !== false;
      const blockAlign = styles.textAlign || 'center';

      let dateDisplay = '';
      if (targetDate) {
        try {
          const d = new Date(targetDate);
          dateDisplay = d.toLocaleDateString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
          });
        } catch { /* ignore */ }
      }

      const unitLabels = ['Days', 'Hours', 'Minutes', 'Seconds'];
      const unitCells = unitLabels.map((label) => `
        <td align="center" valign="top" style="padding: 0 6px; text-align: center; min-width: 60px;">
          <div style="background-color: ${styles.backgroundColor || '#FFFFFF'}; border: 1px solid ${styles.borderColor || '#E5E7EB'}; border-radius: 8px; padding: 12px 16px; display: inline-block; min-width: 60px;">
            <div style="font-size: ${styles.fontSize || '32px'}; font-weight: ${styles.fontWeight || '700'}; color: ${styles.color || '#111827'}; font-family: ${fontFamily}; line-height: 1.2;">00</div>
            ${showLabels ? `<div style="font-size: 11px; font-weight: 500; color: #6B7280; text-transform: uppercase; letter-spacing: 0.5px; font-family: ${fontFamily}; margin-top: 4px;">${label}</div>` : ''}
          </div>
        </td>
      `).join('');

      return `
        <div style="padding: ${styles.padding || '20px'}; text-align: ${blockAlign}; background-color: transparent; font-family: ${fontFamily};">
          ${title ? `<div style="font-size: 18px; font-weight: 600; color: ${styles.color || '#111827'}; font-family: ${fontFamily}; margin-bottom: 16px; text-align: ${blockAlign};">${escapeHtml(title)}</div>` : ''}
          <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="${blockAlign}" style="margin: 0 ${blockAlign === 'center' ? 'auto' : blockAlign === 'right' ? '0 0 0 auto' : '0'};">
            <tr>${unitCells}</tr>
          </table>
          ${dateDisplay ? `<div style="font-size: 13px; color: #6B7280; font-family: ${fontFamily}; margin-top: 12px; text-align: ${blockAlign};">${escapeHtml(dateDisplay)}</div>` : ''}
          ${dateDisplay ? `<div style="font-size: 11px; color: #9CA3AF; font-family: ${fontFamily}; margin-top: 4px; text-align: ${blockAlign};">Date shown above — countdown not supported in email</div>` : `<div style="font-size: 11px; color: #9CA3AF; font-family: ${fontFamily}; margin-top: 6px; text-align: ${blockAlign};">Set a target date to display the deadline</div>`}
        </div>
      `;
    }

    case 'code':
      return `
        <div style="padding: ${styles.padding || '20px'}; background-color: ${styles.backgroundColor || '#1F2937'}; color: ${styles.color || '#F9FAFB'}; font-family: ${formatFontFamily(styles.fontFamily, 'monospace')}; font-size: 14px; border-radius: 4px; overflow: auto;">
          <pre style="margin: 0; white-space: pre-wrap;">${escapeHtml(content.code || '<div>Custom HTML code</div>')}</pre>
        </div>
      `;

    case 'apps': {
      const appStoreUrl = content.appStoreUrl || '';
      const googlePlayUrl = content.googlePlayUrl || '';
      const align = styles.textAlign || 'center';
      const textAlign = align === 'left' ? 'left' : align === 'right' ? 'right' : 'center';
      const badgeHeight = content.badgeHeight || '44';

      // SVG badges encoded as data URIs for email-safe inline rendering
      const appStoreBadge = `https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg`;
      const googlePlayBadge = `https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png`;

      const appStoreLinkHtml = appStoreUrl
        ? `<a href="${escapeHtml(appStoreUrl)}" style="display: inline-block; margin: 0 6px 8px 0; text-decoration: none;" target="_blank">
            <img src="${appStoreBadge}" alt="Download on the App Store" height="${badgeHeight}" style="height: ${badgeHeight}px; width: auto; display: inline-block; border: 0;" />
          </a>`
        : `<span style="display: inline-block; margin: 0 6px 8px 0; padding: 10px 16px; background-color: #000000; color: #FFFFFF; font-family: ${fontFamily}; font-size: 13px; border-radius: 6px; vertical-align: middle;">🍎 App Store</span>`;

      const googlePlayLinkHtml = googlePlayUrl
        ? `<a href="${escapeHtml(googlePlayUrl)}" style="display: inline-block; margin: 0 6px 8px 0; text-decoration: none;" target="_blank">
            <img src="${googlePlayBadge}" alt="Get it on Google Play" height="${badgeHeight}" style="height: ${badgeHeight}px; width: auto; display: inline-block; border: 0;" />
          </a>`
        : `<span style="display: inline-block; margin: 0 0 8px 0; padding: 10px 16px; background-color: #414141; color: #FFFFFF; font-family: ${fontFamily}; font-size: 13px; border-radius: 6px; vertical-align: middle;">▶ Google Play</span>`;

      const showAppStore = content.showAppStore !== false;
      const showGooglePlay = content.showGooglePlay !== false;
      const titleHtml = content.title
        ? `<div style="font-size: 15px; font-weight: 600; color: ${styles.color || '#111827'}; font-family: ${fontFamily}; margin-bottom: 12px; text-align: ${textAlign};">${escapeHtml(content.title)}</div>`
        : '';

      return `
        <div style="padding: ${styles.padding || '20px'}; text-align: ${textAlign}; background-color: ${styles.backgroundColor || 'transparent'};">
          ${titleHtml}
          <div style="display: inline-block;">
            ${showAppStore ? appStoreLinkHtml : ''}
            ${showGooglePlay ? googlePlayLinkHtml : ''}
          </div>
        </div>
      `;
    }

    case 'product': {
      const template = content.template || 'standard';
      const isMobile = options.isMobile === true;

      if (template === 'editorial') {
        const outerPadding = computePadding(styles, '0px');
        const contentPadding = '80px 40px';
        const nameFontSize = '42px';
        const priceFontSize = '24px';
        const serifFont = "'Playfair Display', Georgia, serif";

        const subtitleHtml = content.subtitle
          ? `<p style="margin: 0 0 12px 0; font-size: 12px; font-weight: 600; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.25em; font-family: ${fontFamily}; text-align: ${styles.textAlign || 'center'};">${escapeHtml(content.subtitle)}</p>`
          : '';
        const headingHtml = content.name
          ? `<h2 class="editorial-product-name" style="margin: 0 0 16px 0; font-size: ${nameFontSize}; font-weight: 400; color: #111827; line-height: 1.2; font-family: ${serifFont}; text-align: ${styles.textAlign || 'center'};">${escapeHtml(content.name)}</h2>`
          : '';
        const priceHtml = `<div class="editorial-product-price" style="font-size: ${priceFontSize}; font-weight: 400; color: #6B7280; margin-bottom: 24px; font-family: ${serifFont}; text-align: ${styles.textAlign || 'center'};">${escapeHtml(content.price || '$149.00')}</div>`;
        const textHtml = content.description
          ? `<p class="editorial-product-desc" style="margin: 0 0 40px 0; font-size: 16px; color: #4B5563; line-height: 1.7; font-family: ${fontFamily}; text-align: ${styles.textAlign || 'center'};">${escapeHtml(content.description)}</p>`
          : '';
        const btnBg = content.buttonColor || '#111827';
        const btnText = content.buttonTextColor || '#FFFFFF';
        const btnPad = content.buttonPadding || '16px 48px';
        const productEditorialAlign = styles.textAlign || 'center';
        const buttonHtml = content.buttonText
          ? `<div style="text-align: ${productEditorialAlign};"><a href="${escapeHtml(content.url || '#')}" style="display: inline-block; background-color: ${btnBg}; color: ${btnText}; padding: ${btnPad}; border-radius: 0; font-size: 13px; font-weight: 600; text-decoration: none; text-transform: uppercase; letter-spacing: 0.15em;">${escapeHtml(content.buttonText)}</a></div>`
          : '';
        const imageHtml = content.image
          ? `<img src="${escapeHtml(content.image)}" alt="${escapeHtml(content.name)}" style="width: 100%; height: auto; display: block; border: 0;" />`
          : '';

        return `
          <div style="padding: ${outerPadding}; background-color: ${styles.backgroundColor || '#FFFFFF'}; text-align: ${styles.textAlign || 'center'}; width: 100%; box-sizing: border-box;">
            ${imageHtml}
            <div class="editorial-product-body" style="max-width: 600px; margin: 0 auto; padding: ${contentPadding}; box-sizing: border-box;">
              ${subtitleHtml}${headingHtml}${priceHtml}${textHtml}${buttonHtml}
            </div>
          </td></tr></table>
        `;
      }

      const productPadding = isMobile ? '20px' : '24px';
      const containerPadding = isMobile ? '20px' : '24px';
      const imageMarginBottom = isMobile ? '16px' : '20px';
      const descMarginBottom = isMobile ? '10px' : '12px';
      const priceMarginBottom = isMobile ? '12px' : '16px';
      const oldPriceFontSize = isMobile ? '12px' : '14px';

      const productNameFontSize = isMobile ? '18px' : '20px';
      const productDescFontSize = isMobile ? '13px' : '14px';
      const productPriceFontSize = isMobile ? '20px' : '24px';
      const buttonBg = styles.buttonStyle?.backgroundColor || '#10B981';
      const buttonColor = styles.buttonStyle?.color || '#FFFFFF';
      const buttonPadding = isMobile ? '12px 24px' : (styles.buttonStyle?.padding || '12px 32px');
      const buttonRadius = styles.buttonStyle?.borderRadius || '6px';
      const buttonFontSize = isMobile ? '14px' : (styles.buttonStyle?.fontSize || '14px');
      const buttonFontWeight = styles.buttonStyle?.fontWeight || '600';

      return `
        <div style="padding: ${styles.padding || containerPadding}; background-color: ${styles.backgroundColor || '#FFFFFF'}; font-family: ${fontFamily}; width: 100%; box-sizing: border-box;">
          <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center" style="max-width: ${isMobile ? '100%' : '480px'}; width: 100%;">
            <tr>
              <td align="center" valign="top" style="text-align: center; vertical-align: top;">
                <div style="background-color: #F3F4F6; border-radius: 8px; margin-bottom: ${imageMarginBottom}; overflow: hidden;">
                  <img src="${content.image || 'https://via.placeholder.com/480x220'}" alt="${escapeHtml(content.name || 'Product')}" style="width: 100%; height: auto; display: block;" />
                </div>
                <div style="font-weight: 700; font-size: ${productNameFontSize}; margin-bottom: 6px; color: #111827;">
                  ${escapeHtml(content.name || 'Product name')}
                </div>
                <div style="font-size: ${productDescFontSize}; color: #4B5563; line-height: 1.6; margin-bottom: ${descMarginBottom};">
                  ${escapeHtml(content.description || 'Describe your product in a way that highlights how it will benefit your readers.')}
                </div>
                <div style="margin-bottom: ${priceMarginBottom};">
                  <span style="font-size: ${productPriceFontSize}; font-weight: 700; color: #111827; margin-right: 12px;">
                    ${escapeHtml(content.price || '$99')}
                  </span>
                  ${content.oldPrice ? `<span style="font-size: ${oldPriceFontSize}; color: #9CA3AF; text-decoration: line-through;">${escapeHtml(content.oldPrice)}</span>` : ''}
                </div>
                <a href="${content.url || '#'}" style="display: inline-block; padding: ${buttonPadding}; background-color: ${buttonBg}; color: ${buttonColor}; text-decoration: none; border-radius: ${buttonRadius}; font-size: ${buttonFontSize}; font-weight: ${buttonFontWeight};">
                  ${escapeHtml(content.buttonText || 'Button')}
                </a>
              </td>
            </tr>
          </table>
        </div>
      `;
    }

    case 'product-rec': {
      const isMobile = options.isMobile === true;
      const products = Array.isArray(content.products) ? content.products : [];
      const productsPerRow = isMobile ? 1 : (content.productsPerRow || 3);
      const isCenteringNeeded = !isMobile && productsPerRow === 3 && products.length === 2;

      const colWidth = isCenteringNeeded ? '50%' : `${Math.floor(100 / productsPerRow)}%`;
      const tableMaxWidth = isCenteringNeeded ? '400px' : '600px';

      const rows = [];
      const rowSize = productsPerRow;
      for (let i = 0; i < products.length; i += rowSize) {
        rows.push(products.slice(i, i + rowSize));
      }

      const imgHeight = productsPerRow === 3 ? '180px' : productsPerRow === 2 ? '280px' : '580px';

      const productRowsHtml = rows.map((row) => {
        const cornerRadius = content.showRoundedCorners ? '12px' : '0';

        const rowCells = row.map((product: any) => `
          <td align="center" valign="top" style="padding: 0 10px 40px 10px; vertical-align: top; width: ${colWidth};" width="${colWidth.replace('%', '')}">
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
              <tr>
                <td align="${styles.textAlign || 'center'}" valign="top" style="vertical-align: top; text-align: ${styles.textAlign || 'center'};">
                  <div style="background-color: #F3F4F6; border-radius: ${cornerRadius}; margin-bottom: 16px; overflow: hidden; height: ${imgHeight}; width: 100%;">
                    <img src="${product.image || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=400&q=80'}" alt="${escapeHtml(product.name || 'Product')}" style="width: 100%; height: ${imgHeight}; border-radius: ${cornerRadius}; display: block; object-fit: cover; border: 0;" onerror="this.src='https://via.placeholder.com/400x400?text=Product';" />
                  </div>
                  <div style="font-size: ${styles.productNameFontSize || '16px'}; font-weight: ${styles.productNameFontWeight || '600'}; color: ${styles.productNameColor || '#111827'}; font-family: ${formatFontFamily(styles.productNameFontFamily, fontFamily)}; margin-bottom: 8px; line-height: 1.4; text-align: ${styles.textAlign || 'center'};">
                    ${escapeHtml(product.name || 'Product name')}
                  </div>
                  <div style="margin-bottom: 16px; text-align: ${styles.textAlign || 'center'};">
                    <span style="font-size: ${styles.productPriceFontSize || '15px'}; font-weight: ${styles.productPriceFontWeight || '700'}; color: ${styles.productPriceColor || '#111827'}; font-family: ${formatFontFamily(styles.productPriceFontFamily, fontFamily)}; margin-right: 8px;">
                      ${escapeHtml(product.price || '$0.00')}
                    </span>
                    ${product.oldPrice
            ? `<span style="font-size: 13px; color: #9CA3AF; text-decoration: line-through;">
                      ${escapeHtml(product.oldPrice)}
                    </span>`
            : ''
          }
                  </div>
                  <a href="${escapeHtml(product.url || '#')}" style="display: inline-block; padding: 12px 24px; background-color: #111827; color: #FFFFFF; text-decoration: none; border-radius: 4px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;">
                    ${escapeHtml(product.buttonText || 'SHOP NOW')}
                  </a>
                </td>
              </tr>
            </table>
          </td>
        `).join('');

        const emptyCellsCount = rowSize - row.length;
        const emptyCellsStr = !isCenteringNeeded && emptyCellsCount > 0
          ? Array(emptyCellsCount).fill(`<td width="${colWidth.replace('%', '')}" style="padding: 0 10px; width: ${colWidth};">&nbsp;</td>`).join('')
          : '';

        return `<tr>${rowCells}${emptyCellsStr}</tr>`;
      }).join('');

      return `
        <div style="padding: ${styles.padding || '48px 24px'}; background-color: ${styles.backgroundColor || '#FFFFFF'}; font-family: ${fontFamily};">
          <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" align="center" style="width: 100%; max-width: ${tableMaxWidth}; margin: 0 auto; border-collapse: collapse;">
            ${productRowsHtml}
          </table>
        </div>
      `;
    }
    case 'header':
      return renderHeaderBlockHtml(content, styles, fontFamily, options.assetOrigin, options.isMobile === true);

    case 'qr-code': {
      const rawPayload = (content.url || 'https://example.com').trim() || 'https://example.com';
      const width = content.width || 150;
      const fg = normalizeHexColorForQr(content.foregroundColor, '#000000');
      const bg = normalizeHexColorForQr(content.backgroundColor, '#FFFFFF');
      const embedded = buildQrCodeDataUrlForEmail(rawPayload, {
        displayWidth: width,
        foregroundColor: fg,
        backgroundColor: bg,
      });
      const fgApi = fg.replace('#', '');
      const bgApi = bg.replace('#', '');
      const qrSrc =
        embedded ||
        `https://api.qrserver.com/v1/create-qr-code/?size=${width}x${width}&data=${encodeURIComponent(rawPayload)}&color=${fgApi}&bgcolor=${bgApi}&margin=0`;

      const align = content.alignment || 'center';
      const wrapperAlign = align === 'left' ? 'left' : align === 'right' ? 'right' : 'center';

      return `
        <div style="padding: ${styles.padding || '20px'}; text-align: ${wrapperAlign}; background-color: ${styles.backgroundColor || '#FFFFFF'};">
          <img src="${escapeHtml(qrSrc)}" alt="QR Code" width="${width}" height="${width}" style="display: inline-block; border: 0;" />
        </div>
      `;
    }

    case 'star-rating': {
      const rating = Math.min(Math.max(0, content.rating || 5), content.maxRating || 5);
      const maxRating = content.maxRating || 5;
      const size = content.size || 24;
      const color = content.color || '#FBBF24';
      const emptyColor = content.emptyColor || '#E5E7EB';
      const align = content.alignment || 'center';
      const wrapperAlign = align === 'left' ? 'left' : align === 'right' ? 'right' : 'center';

      let starsHtml = '';
      for (let i = 0; i < maxRating; i++) {
        const isFilled = i < rating;
        const starColor = isFilled ? color : emptyColor;
        // Using HTML entity for star to ensure broad client support without external images
        starsHtml += `<span style="font-size: ${size}px; line-height: 1; color: ${starColor}; margin-right: 2px;">&#9733;</span>`;
      }

      return `
        <div style="padding: ${styles.padding || '20px'}; text-align: ${wrapperAlign}; background-color: ${styles.backgroundColor || '#FFFFFF'}; font-family: ${fontFamily};">
          ${starsHtml}
        </div>
      `;
    }

    case 'poll': {
      const question = content.question || '';
      const pollOptions = (content.options || []) as any[];
      const pollTracking = options.pollTracking;

      // Generate tracking URL or use original URL
      const generatePollUrl = (option: any, index: number) => {
        if (pollTracking && pollTracking.workspaceId && pollTracking.campaignId) {
          // Use tracking URL with merge tag for email
          const baseUrl = pollTracking.baseUrl || 'https://us-central1-formulabot-1d12b.cloudfunctions.net';
          const params = new URLSearchParams({
            w: pollTracking.workspaceId,
            c: pollTracking.campaignId,
            b: block.id,
            o: index.toString(),
            l: encodeURIComponent(option.label || `Option ${index + 1}`),
            e: '{{email}}', // Merge tag to be replaced when sending
            r: option.url || ''
          });
          return `${baseUrl}/recordPollResponse?${params.toString()}`;
        }
        return option.url || '#';
      };

      const optionsHtml = pollOptions.map((option, index) => `
        <a href="${generatePollUrl(option, index)}" style="display: block; width: 100%; max-width: 300px; padding: 12px 20px; margin: 0 auto 10px auto; background-color: #F3F4F6; color: #1F2937; text-decoration: none; border-radius: 6px; text-align: center; border: 1px solid #E5E7EB; font-weight: 500; font-family: ${fontFamily};">
          ${escapeHtml(option.label || 'Option')}
        </a>
      `).join('');

      return `
        <div style="padding: ${styles.padding || '20px'}; text-align: ${styles.textAlign || 'center'}; background-color: ${styles.backgroundColor || '#FFFFFF'}; font-family: ${fontFamily};">
          <div style="margin-bottom: 20px; font-weight: bold; font-size: 18px; color: #111827; text-align: ${styles.textAlign || 'center'};">${escapeHtml(question)}</div>
          <div style="width: 100%;">
            ${optionsHtml}
          </div>
        </div>
      `;
    }

    case 'music-launch': {
      const layout = content.layout || 'dark';
      const bgColor = styles.backgroundColor || (layout === 'dark' ? '#020617' : layout === 'tour' ? '#F8FAFC' : '#FFFFFF');
      // Use custom text color from styles if provided, otherwise use layout defaults
      // Make text brighter on dark backgrounds for better contrast
      const textColor = styles.color || (layout === 'dark' ? '#FFFFFF' : '#111827');
      const mutedColor = layout === 'dark' ? '#D1D5DB' : layout === 'tour' ? '#64748B' : '#6B7280';
      const showDescription =
        content.showDescription !== false && !!(content.description && content.description.trim());
      const tracks = Array.isArray(content.tracks)
        ? content.tracks.filter((t: any) => t && (t.title || t.duration))
        : [];
      const showTracklist = content.showTracklist !== false && tracks.length > 0;
      const platforms = content.platforms || {};
      const showPlatforms =
        content.showPlatforms !== false &&
        !!(
          (platforms.spotifyUrl && platforms.spotifyUrl.trim()) ||
          (platforms.appleMusicUrl && platforms.appleMusicUrl.trim()) ||
          (platforms.youtubeUrl && platforms.youtubeUrl.trim()) ||
          (platforms.soundcloudUrl && platforms.soundcloudUrl.trim()) ||
          (platforms.bandcampUrl && platforms.bandcampUrl.trim())
        );
      const showSecondaryButton = content.showSecondaryButton && !!content.secondaryButtonText;
      const isMobile = options.isMobile === true;

      const sectionPadding = isMobile ? '24px 20px' : '32px 32px';
      const heroTopPadding = isMobile
        ? (layout === 'light' || layout === 'tour' ? '24px' : '32px')
        : (layout === 'light' ? '40px' : '48px');
      const heroSidePadding = isMobile ? '20px' : '32px';
      const heroBottomPadding = isMobile ? '24px' : '32px';

      // For light/tour layout: side-by-side cover image and text
      const heroLeftColumn =
        content.coverImage && (layout === 'light' || layout === 'tour')
          ? `<td align="left" valign="top" style="width: 50%; max-width: 50%; padding: ${heroTopPadding} ${heroSidePadding} ${heroBottomPadding} 0; box-sizing: border-box;">
        <div style="border-radius: ${layout === 'tour' ? '12px' : '16px'}; overflow: hidden; box-shadow: ${layout === 'tour' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 10px 30px rgba(15,23,42,0.15)'}; max-height: ${layout === 'tour' ? '400px' : '360px'}; max-width: 100%; box-sizing: border-box;">
          <img src="${escapeHtml(content.coverImage)}" alt="${escapeHtml(
            content.title || 'Album cover'
          )}" style="width: 100%; max-width: 100%; height: auto; display: block; object-fit: cover;" />
        </div>
      </td>`
          : '';

      const heroTextColumn = `
      <td align="${layout === 'light' || layout === 'tour' ? 'left' : 'center'}" valign="middle" style="width: ${layout === 'light' || layout === 'tour' ? '50%' : '100%'}; max-width: ${layout === 'light' || layout === 'tour' ? '50%' : '100%'}; padding: ${heroTopPadding} ${layout === 'light' || layout === 'tour' ? `0 ${heroSidePadding} ${heroBottomPadding} ${heroSidePadding}` : `${heroSidePadding} ${heroSidePadding} ${heroBottomPadding} ${heroSidePadding}`
        }; color: ${textColor}; box-sizing: border-box;">
        ${content.releaseType
          ? `<div style="display: inline-block; padding: 4px 10px; border-radius: 999px; background-color: ${layout === 'dark' ? '#1F2937' : layout === 'tour' ? '#E0E7FF' : '#EFF6FF'
          }; color: ${layout === 'tour' ? '#4F46E5' : mutedColor}; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 12px;">
            <span style="display: inline-block; width: 6px; height: 6px; border-radius: 999px; background-color: ${layout === 'tour' ? '#6366F1' : '#22C55E'}; margin-right: 8px; vertical-align: middle;"></span>
            ${escapeHtml(content.releaseType)}
            ${content.releaseDate && content.releaseDate.trim()
            ? `<span style="margin-left: 8px; opacity: 0.8;">• ${escapeHtml(content.releaseDate)}</span>`
            : ''
          }
          </div>`
          : ''
        }
        ${content.title
          ? `<h1 style="margin: 0 0 12px 0; font-size: 32px; font-weight: 800; letter-spacing: -0.02em; color: ${textColor};">
            ${escapeHtml(content.title)}
          </h1>`
          : ''
        }
        ${content.artist
          ? `<p style="margin: 0 0 16px 0; font-size: 16px; font-weight: 500; color: ${mutedColor};">
            By ${escapeHtml(content.artist)}
          </p>`
          : ''
        }
        ${showDescription
          ? `<p style="margin: 0 0 20px 0; font-size: 15px; line-height: 170%; color: ${mutedColor};">
            ${escapeHtml(content.description)}
          </p>`
          : ''
        }
        ${content.primaryButtonText || showSecondaryButton
          ? isMobile
            ? `<table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin: 0; width: 100%;">
            ${content.primaryButtonText
              ? `<tr>
                <td align="${layout === 'light' || layout === 'tour' ? 'left' : 'center'}" style="padding: 0 0 12px 0;">
                  <a href="${escapeHtml(
                content.primaryButtonUrl || '#'
              )}" style="display: inline-block; padding: 14px 28px; border-radius: 999px; background-color: ${layout === 'tour' ? '#6366F1' : '#22C55E'}; color: ${layout === 'tour' ? '#FFFFFF' : '#0F172A'}; font-weight: 600; font-size: 15px; text-decoration: none; min-width: 120px; text-align: center; width: 100%; max-width: 100%; box-sizing: border-box;">
                    ${escapeHtml(content.primaryButtonText)}
                  </a>
                </td>
              </tr>`
              : ''}
            ${showSecondaryButton
              ? `<tr>
                <td align="${layout === 'light' || layout === 'tour' ? 'left' : 'center'}" style="padding: 0;">
                  <a href="${escapeHtml(
                content.secondaryButtonUrl || '#'
              )}" style="display: inline-block; padding: 14px 24px; border-radius: 999px; border: 2px solid ${layout === 'dark' ? '#6B7280' : layout === 'tour' ? '#6366F1' : '#D1D5DB'
              }; color: ${layout === 'dark' ? '#FFFFFF' : layout === 'tour' ? '#6366F1' : mutedColor}; font-weight: 600; font-size: 15px; background-color: transparent; text-decoration: none; min-width: 120px; text-align: center; width: 100%; max-width: 100%; box-sizing: border-box;">
                    ${escapeHtml(content.secondaryButtonText || '')}
                  </a>
                </td>
              </tr>`
              : ''}
          </table>`
            : `<table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin: 0;">
            <tr>
              <td align="${layout === 'light' || layout === 'tour' ? 'left' : 'center'}" style="padding: 0;">
                ${content.primaryButtonText
              ? `<a href="${escapeHtml(
                content.primaryButtonUrl || '#'
              )}" style="display: inline-block; padding: 14px 28px; border-radius: 999px; background-color: ${layout === 'tour' ? '#6366F1' : '#22C55E'}; color: ${layout === 'tour' ? '#FFFFFF' : '#0F172A'}; font-weight: 600; font-size: 15px; text-decoration: none; margin-right: 12px; min-width: 120px; text-align: center;">
                    ${escapeHtml(content.primaryButtonText)}
                  </a>`
              : ''
            }
                ${showSecondaryButton
              ? `<a href="${escapeHtml(
                content.secondaryButtonUrl || '#'
              )}" style="display: inline-block; padding: 14px 24px; border-radius: 999px; border: 2px solid ${layout === 'dark' ? '#6B7280' : layout === 'tour' ? '#6366F1' : '#D1D5DB'
              }; color: ${layout === 'dark' ? '#FFFFFF' : layout === 'tour' ? '#6366F1' : mutedColor}; font-weight: 600; font-size: 15px; background-color: transparent; text-decoration: none; min-width: 120px; text-align: center;">
                    ${escapeHtml(content.secondaryButtonText || '')}
                  </a>`
              : ''
            }
              </td>
            </tr>
          </table>`
          : ''
        }
      </td>
    `;

      // For dark layout: cover image centered above text
      const coverRow =
        content.coverImage && layout === 'dark'
          ? `
      <tr>
        <td align="center" valign="top" style="padding: ${heroTopPadding} ${heroSidePadding} 24px ${heroSidePadding};">
          <div style="border-radius: 16px; overflow: hidden; box-shadow: 0 20px 40px rgba(0,0,0,0.5); max-width: 600px; margin: 0 auto;">
            <img src="${escapeHtml(content.coverImage)}" alt="${escapeHtml(
            content.title || 'Album cover'
          )}" style="width: 100%; height: auto; display: block; max-height: 360px; object-fit: cover;" />
          </div>
        </td>
      </tr>
    `
          : '';

      const heroRow =
        layout === 'light' || layout === 'tour'
          ? `
      <tr>
        ${heroLeftColumn}
        ${heroTextColumn}
      </tr>
    `
          : `
      <tr>
        ${heroTextColumn}
      </tr>
    `;

      // Tour Dates Section - Only for tour layout
      const tourDatesHtml = layout === 'tour' && tracks.length > 0
        ? `
      <tr>
        <td style="padding: ${sectionPadding}; border-top: 1px solid #E2E8F0; background-color: #FFFFFF; margin-top: 24px;">
          <h2 style="margin: 0 0 20px 0; font-size: 20px; font-weight: 700; color: #111827;">
            📅 Tour Dates
          </h2>
          <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
            ${tracks.slice(0, 5).map((track: any, idx: number) => `
              <tr>
                <td style="padding: 16px; background-color: #F8FAFC; border-radius: 8px; border: 1px solid #E2E8F0; margin-bottom: 12px;">
                  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                    <tr>
                      <td style="padding: 0;">
                        <div style="font-size: 16px; font-weight: 600; color: #111827; margin-bottom: 4px;">
                          ${escapeHtml(track.title || `Date ${idx + 1}`)}
                        </div>
                        ${track.duration ? `<div style="font-size: 14px; color: #64748B;">${escapeHtml(track.duration)}</div>` : ''}
                        ${track.featured ? `<div style="font-size: 13px; color: #6366F1; margin-top: 4px;">📍 ${escapeHtml(track.featured)}</div>` : ''}
                      </td>
                      <td align="right" valign="middle" style="padding: 0; white-space: nowrap;">
                        <a href="${escapeHtml(content.primaryButtonUrl || '#')}" style="display: inline-block; padding: 8px 16px; border-radius: 6px; background-color: #6366F1; color: #FFFFFF; font-weight: 600; font-size: 13px; text-decoration: none;">
                          Get Tickets
                        </a>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            `).join('')}
          </table>
        </td>
      </tr>
    `
        : '';

      const tracklistHtml = showTracklist && layout !== 'tour'
        ? `
      <tr>
        <td style="padding: ${sectionPadding}; border-top: 1px solid ${layout === 'dark' ? '#1F2937' : '#E5E7EB'
        }; background-color: ${layout === 'dark' ? '#020617' : '#FFFFFF'};">
          <h2 style="margin: 0 0 16px 0; font-size: 18px; font-weight: 600; color: ${textColor};">Tracklist</h2>
          <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
            <tbody>
              ${tracks
          .map(
            (track: any, idx: number) => `
                <tr>
                  <td style="padding: 8px 0; font-size: 14px; color: ${textColor}; border-bottom: 1px solid ${layout === 'dark' ? '#1F2937' : '#E5E7EB'
              };">
                    <span style="margin-right: 12px; opacity: 0.7;">${idx + 1}.</span>
                    <span style="font-weight: 500;">${escapeHtml(track.title || `Track ${idx + 1}`)}</span>
                    ${track.featured
                ? `<span style="margin-left: 8px; color: ${mutedColor}; font-size: 12px;">(feat. ${escapeHtml(
                  track.featured
                )})</span>`
                : ''
              }
                  </td>
                  ${track.duration
                ? `<td style="padding: 8px 0; font-size: 13px; color: ${mutedColor}; text-align: right; border-bottom: 1px solid ${layout === 'dark' ? '#1F2937' : '#E5E7EB'
                }; white-space: nowrap;">
                    ${escapeHtml(track.duration)}
                  </td>`
                : ''
              }
                </tr>
              `
          )
          .join('')}
            </tbody>
          </table>
        </td>
      </tr>
    `
        : '';

      const platformButtons: string[] = [];
      if (platforms.spotifyUrl && platforms.spotifyUrl.trim()) {
        platformButtons.push(
          `<a href="${escapeHtml(
            platforms.spotifyUrl
          )}" style="display: inline-block; padding: 8px 16px; border-radius: 999px; background-color: ${layout === 'dark' ? '#16A34A' : '#DCFCE7'
          }; color: ${layout === 'dark' ? '#ECFDF5' : '#166534'}; font-size: 13px; font-weight: 600; text-decoration: none; margin-right: 8px; margin-bottom: 8px;">
            🎧 Spotify
          </a>`
        );
      }
      if (platforms.appleMusicUrl && platforms.appleMusicUrl.trim()) {
        platformButtons.push(
          `<a href="${escapeHtml(
            platforms.appleMusicUrl
          )}" style="display: inline-block; padding: 8px 16px; border-radius: 999px; background-color: ${layout === 'dark' ? '#DB2777' : '#FCE7F3'
          }; color: ${layout === 'dark' ? '#FDF2F8' : '#9D174D'}; font-size: 13px; font-weight: 600; text-decoration: none; margin-right: 8px; margin-bottom: 8px;">
             Apple Music
          </a>`
        );
      }
      if (platforms.youtubeUrl && platforms.youtubeUrl.trim()) {
        platformButtons.push(
          `<a href="${escapeHtml(
            platforms.youtubeUrl
          )}" style="display: inline-block; padding: 8px 16px; border-radius: 999px; background-color: ${layout === 'dark' ? '#DC2626' : '#FEE2E2'
          }; color: ${layout === 'dark' ? '#FEE2E2' : '#B91C1C'}; font-size: 13px; font-weight: 600; text-decoration: none; margin-right: 8px; margin-bottom: 8px;">
            ▶ YouTube
          </a>`
        );
      }
      if (platforms.soundcloudUrl && platforms.soundcloudUrl.trim()) {
        platformButtons.push(
          `<a href="${escapeHtml(
            platforms.soundcloudUrl
          )}" style="display: inline-block; padding: 8px 16px; border-radius: 999px; background-color: ${layout === 'dark' ? '#F97316' : '#FFEDD5'
          }; color: ${layout === 'dark' ? '#FFEDD5' : '#9A3412'}; font-size: 13px; font-weight: 600; text-decoration: none; margin-right: 8px; margin-bottom: 8px;">
            ☁️ SoundCloud
          </a>`
        );
      }
      if (platforms.bandcampUrl && platforms.bandcampUrl.trim()) {
        platformButtons.push(
          `<a href="${escapeHtml(
            platforms.bandcampUrl
          )}" style="display: inline-block; padding: 8px 16px; border-radius: 999px; background-color: ${layout === 'dark' ? '#0F766E' : '#CCFBF1'
          }; color: ${layout === 'dark' ? '#ECFEFF' : '#0F766E'}; font-size: 13px; font-weight: 600; text-decoration: none; margin-right: 8px; margin-bottom: 8px;">
            ♪ Bandcamp
          </a>`
        );
      }

      const platformsHtml = showPlatforms
        ? `
      <tr>
        <td style="padding: ${sectionPadding}; border-top: 1px solid ${layout === 'dark' ? '#1F2937' : layout === 'tour' ? '#E2E8F0' : '#E5E7EB'
        }; background-color: ${layout === 'dark' ? '#030712' : layout === 'tour' ? '#F8FAFC' : '#F9FAFB'};">
          <h2 style="margin: 0 0 16px 0; font-size: 18px; font-weight: 600; color: ${textColor};">
            Listen on your favorite platform
          </h2>
          <div style="margin-top: 8px;">
            ${platformButtons.join('')}
          </div>
        </td>
      </tr>
    `
        : '';

      const heroTable =
        layout === 'light' || layout === 'tour'
          ? `
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
        <tr>
          ${heroLeftColumn}
          ${heroTextColumn}
        </tr>
      </table>
    `
          : `
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
        ${coverRow}
        ${heroRow}
      </table>
    `;

      return `
      <div style="padding: ${styles.padding || '0 0 0 0'}; background-color: ${bgColor}; font-family: ${fontFamily}; color: ${textColor}; width: 100%; box-sizing: border-box;">
        ${heroTable}
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
          ${tourDatesHtml}
          ${tracklistHtml}
          ${platformsHtml}
        </table>
      </div>
    `;
    }
    case 'newsletter': {
      const layout = content.layout || 'classic';
      const isMobile = options.isMobile === true;
      const bgColor = styles.backgroundColor || '#FFFFFF';
      const textColor = '#111827';
      const mutedColor = '#4B5563';
      const sections =
        Array.isArray(content.sections) && content.sections.length
          ? content.sections.filter(
            (section: any) =>
              section &&
              (section.enabled !== false) &&
              (section.title || section.summary)
          )
          : [];

      const introPadding = isMobile ? '24px 20px' : '24px 32px';
      const imageMarginBottom = isMobile ? '16px' : '20px';
      const titleFontSize = isMobile ? '20px' : '24px';
      const introFontSize = isMobile ? '13px' : '14px';
      const buttonFontSize = isMobile ? '12px' : '13px';
      const buttonPadding = isMobile ? '10px 18px' : '10px 20px';
      const sectionTitleFontSize = isMobile ? '13px' : '14px';
      const sectionSummaryFontSize = isMobile ? '12px' : '13px';
      const sectionPadding = isMobile ? '12px 0' : '12px 12px 12px 0';
      const sectionsContainerPadding = isMobile ? '8px 20px 24px 20px' : '8px 24px 32px 24px';

      const introImageHtml =
        content.showImage !== false && content.image
          ? `
        <tr>
          <td style="padding-bottom: ${imageMarginBottom};">
            <img src="${escapeHtml(content.image)}" alt="${escapeHtml(
            content.title || 'Newsletter'
          )}" style="width: 100%; height: auto; display: block; border-radius: 12px;" />
          </td>
        </tr>
      `
          : '';

      const titleHtml = content.title
        ? `
        <tr>
          <td style="padding: 0 0 8px 0; font-size: ${titleFontSize}; font-weight: 700; letter-spacing: -0.01em; color: ${textColor}; font-family: ${fontFamily};">
            ${escapeHtml(content.title)}
          </td>
        </tr>
      `
        : '';

      const introHtml = content.intro
        ? `
        <tr>
          <td style="padding: 0 0 12px 0; font-size: ${introFontSize}; line-height: 1.7; color: ${mutedColor};">
            ${escapeHtml(content.intro)}
          </td>
        </tr>
      `
        : '';

      const buttonHtml =
        content.showButton && content.buttonText
          ? `
        <tr>
          <td style="padding: 8px 0 0 0;">
            <a href="${escapeHtml(
            content.buttonUrl || '#'
          )}" style="display: inline-block; padding: ${buttonPadding}; border-radius: 999px; background-color: #111827; color: #FFFFFF; text-decoration: none; font-size: ${buttonFontSize}; font-weight: 600; font-family: ${fontFamily};">
              ${escapeHtml(content.buttonText)}
            </a>
          </td>
        </tr>
      `
          : '';

      const sectionsHtml =
        content.showSections !== false && sections.length > 0
          ? `
        <tr>
          <td style="padding: ${sectionsContainerPadding};">
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
              ${isMobile ? sections.map((section: any) => `
                <tr>
                  <td valign="top" style="padding: ${sectionPadding}; border-top: 1px solid #E5E7EB; width: 100%;">
                    ${section.title ? `<div style="font-size: ${sectionTitleFontSize}; font-weight: 600; margin-bottom: 4px; color: ${textColor};">${escapeHtml(section.title)}</div>` : ''}
                    ${section.summary ? `<div style="font-size: ${sectionSummaryFontSize}; line-height: 1.6; color: ${mutedColor};">${escapeHtml(section.summary)}</div>` : ''}
                  </td>
                </tr>
              `).join('') : `
              <tr>
                ${sections.map((section: any) => `
                  <td valign="top" style="padding: ${sectionPadding}; border-top: 1px solid #E5E7EB; width: ${layout === 'classic' ? `${Math.floor(100 / sections.length)}%` : '100%'};">
                    ${section.title ? `<div style="font-size: ${sectionTitleFontSize}; font-weight: 600; margin-bottom: 4px; color: ${textColor};">${escapeHtml(section.title)}</div>` : ''}
                    ${section.summary ? `<div style="font-size: ${sectionSummaryFontSize}; line-height: 1.6; color: ${mutedColor};">${escapeHtml(section.summary)}</div>` : ''}
                  </td>
                `).join('')}
              </tr>
              `}
            </table>
          </td>
        </tr>
      `
          : '';

      return `
      <div style="padding: ${styles.padding || '0'}; background-color: ${bgColor}; font-family: ${fontFamily}; color: ${textColor}; width: 100%; box-sizing: border-box;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
          <tr>
            <td style="padding: ${introPadding}; text-align: left;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                ${introImageHtml}
                ${titleHtml}
                ${introHtml}
                ${buttonHtml}
              </table>
            </td>
          </tr>
          ${sectionsHtml}
        </table>
      </div>
    `;
    }

    case 'hero': {
      const isMobile = options.isMobile === true;
      const hasBgImage = !!content.backgroundImage;
      const heroBgColor = styles.backgroundColor || '#F8FAFC';
      const heroBgStyle = hasBgImage
        ? `background-image: url(${escapeHtml(content.backgroundImage)}); background-size: cover; background-position: center; background-color: ${heroBgColor};`
        : `background-color: ${heroBgColor};`;

      const padding = styles.padding || (isMobile ? '40px 20px' : '60px 20px');
      const titleFontSize = isMobile ? '28px' : '36px';
      const subtitleFontSize = isMobile ? '16px' : '18px';
      const buttonPadding = isMobile ? '10px 20px' : '12px 24px';
      const buttonFontSize = isMobile ? '14px' : '16px';
      const minHeight = '200';

      const innerContent = `
        <h1 style="font-size: ${titleFontSize}; font-weight: 700; color: ${styles.color || '#111827'}; margin: 0 0 16px; font-family: ${fontFamily};">
          ${escapeHtml(content.title || 'Welcome to Our Newsletter')}
        </h1>
        ${content.subtitle ? `<p style="font-size: ${subtitleFontSize}; color: ${styles.color ? styles.color : '#6B7280'}; margin: 0 0 24px; font-family: ${fontFamily};">${escapeHtml(content.subtitle)}</p>` : ''}
        ${content.buttonText ? `<a href="${escapeHtml(content.buttonUrl || '#')}" style="display: inline-block; background-color: #3B82F6; color: #FFFFFF; padding: ${buttonPadding}; border-radius: 6px; font-size: ${buttonFontSize}; font-weight: 600; text-decoration: none; font-family: ${fontFamily};">${escapeHtml(content.buttonText)}</a>` : ''}
      `;

      if (hasBgImage) {
        // Use VML for Outlook background image support
        return `
          <div style="padding: ${padding}; text-align: ${styles.textAlign || 'center'}; ${heroBgStyle} mso-padding-alt: ${padding};">
            <!--[if gte mso 9]>
            <v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width:600px; min-height:${minHeight}px;">
              <v:fill type="tile" src="${escapeHtml(content.backgroundImage)}" color="${heroBgColor}" />
              <v:textbox inset="0,0,0,0"><![endif]-->
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr><td align="${styles.textAlign || 'center'}" style="padding: ${padding}; font-family: ${fontFamily};">
                  ${innerContent}
                </td></tr>
              </table>
            <!--[if gte mso 9]></v:textbox></v:rect><![endif]-->
          </div>
        `;
      }

      return `
        <div style="padding: ${padding}; text-align: ${styles.textAlign || 'center'}; ${heroBgStyle}">
          ${innerContent}
        </div>
      `;
    }

    case 'columns': {
      const columnCount = content.columns || 2;
      const columnWidths = content.columnWidths || [];
      const hasCustomWidths = columnWidths.length === columnCount;
      const defaultColumnWidth = `${100 / columnCount}%`;
      /** Matches editor grid gap (~16px default); split across adjacent cells so content is not flush to the column seam. */
      const columnGutterHalfPx = 8;

      const columnCellHorizontalPadding = (index: number) => {
        if (columnCount < 2) return '';
        const padLeft = index > 0 ? columnGutterHalfPx : 0;
        const padRight = index < columnCount - 1 ? columnGutterHalfPx : 0;
        if (padLeft === 0 && padRight === 0) return '';
        return `padding: 0 ${padRight}px 0 ${padLeft}px;`;
      };

      const renderColumnContent = (columnItem: any, index: number) => {
        // Handle empty column
        if (!columnItem || columnItem === '' || (Array.isArray(columnItem) && columnItem.length === 0)) {
          return `
            <div style="border: 1px dashed #E5E7EB; border-radius: 6px; min-height: 160px; background-color: #F9FAFB;"></div>
          `;
        }

        // Handle array of blocks (new format)
        if (Array.isArray(columnItem)) {
          const blocksHtml = columnItem
            .map((blockItem: any, blockIndex: number) => {
              if (!blockItem || !blockItem.type || blockItem.hidden) return '';
              return renderBlockToHTML(
                {
                  id: `col-${index}-block-${blockIndex}`,
                  type: blockItem.type,
                  content: blockItem.content || {},
                  styles: blockItem.styles || {},
                  hidden: blockItem.hidden,
                },
                options,
                globalFontFamily
              );
            })
            .filter(Boolean)
            .join('');

          return `
              <div style="display: block; width: 100%; margin: 0; padding: 0; box-sizing: border-box;">
                ${blocksHtml}
              </div>
            `;
        }

        // Handle legacy string format
        if (typeof columnItem === 'string') {
          return `
            <div style="font-size: 16px; line-height: 1.6; color: #111827; min-height: 160px; padding: 20px 0;">
              ${escapeHtml(columnItem)}
            </div>
          `;
        }

        // Handle single block object (legacy format)
        if (columnItem.type) {
          return renderBlockToHTML(
            {
              id: `col-${index}`,
              type: columnItem.type,
              content: columnItem.content || {},
              styles: columnItem.styles || {},
            },
            options,
            globalFontFamily
          );
        }

        return '';
      };

      // In mobile mode, stack columns vertically
      const isMobile = options.isMobile === true;

      if (isMobile) {
        // Stack columns vertically for mobile
        const stackedColumns = Array.from({ length: columnCount })
          .map((_, index) => {
            const stackGap = index < columnCount - 1 ? columnGutterHalfPx * 2 : 0;
            return `
          <tr>
            <td align="left" valign="top" style="width: 100%; padding: 0; vertical-align: top; display: block;">
              <div style="width: 100%; max-width: 100%; box-sizing: border-box; ${stackGap ? `padding-bottom: ${stackGap}px;` : ''}">
                <div style="display: block; width: 100%; box-sizing: border-box;">
                  ${renderColumnContent(content.columnContent?.[index], index)}
                </div>
              </div>
            </td>
          </tr>
        `;
          })
          .join('');
        return `
          <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="padding: ${styles.padding || '20px'}; background-color: ${styles.backgroundColor || 'transparent'};">
            ${stackedColumns}
          </table>
        `;
      }

      // Desktop: side by side
      const columns = Array.from({ length: columnCount })
        .map((_, index) => {
          const width = hasCustomWidths ? columnWidths[index] : defaultColumnWidth;
          const innerGutter = columnCellHorizontalPadding(index);
          return `
        <td class="column" align="left" valign="top" style="width: ${width}; padding: 0; vertical-align: top; height: 100%;">
          <div style="width: 100%; max-width: 100%; box-sizing: border-box; ${innerGutter}">
            <div style="display: block; width: 100%; box-sizing: border-box;">
              ${renderColumnContent(content.columnContent?.[index], index)}
            </div>
          </td></tr></table>
        </td>
      `;
        })
        .join('');
      return `
        <table role="presentation" class="columns-layout" cellspacing="0" cellpadding="0" border="0" width="100%" style="padding: ${styles.padding || '20px'}; background-color: ${styles.backgroundColor || 'transparent'};">
          <tr>
            ${columns}
          </tr>
        </table>
      `;
    }

    case 'footer': {
      const footerIsMobile = options.isMobile === true;
      const footerStyles = resolveFooterStyles(
        content as Record<string, unknown>,
        styles as Record<string, unknown>,
        fontFamily,
        footerIsMobile,
        false
      );
      return renderFooterBlockHtml(
        content,
        footerStyles,
        fontFamily,
        footerIsMobile
      );
    }

    case 'hero-content': {
      const template = content.template || 'standard';
      const isMobile = options.isMobile === true;
      const coreHeroHtml = renderHeroContentBlockHtml(
        content,
        styles,
        fontFamily,
        isMobile,
        options.assetOrigin
      );
      if (coreHeroHtml) return coreHeroHtml;


      if (template === 'hero-card') {
        const backgroundColor = styles.backgroundColor || '#F3F4F6';
        const cardBackgroundColor = content.cardBackgroundColor || '#FFFFFF';
        const textColor = content.textColor || '#111827';
        const subtitleColor = content.subtitleColor || '#6B7280';
        const buttonColor = content.buttonColor || '#000000';
        const buttonTextColor = content.buttonTextColor || '#FFFFFF';
        const bodyTextColor = content.bodyTextColor || '#4B5563';
        const headingFontSize = '48px';
        const cardOffset = content.cardOffset || '-80px';
        const imageWidth = content.imageWidth || '480px';

        const imageUrl = content.image || 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?auto=format&fit=crop&w=1200&q=80';

        return `
          <div class="editorial-hero-card-wrap" style="background-color: ${backgroundColor}; padding: 0 0 60px 0; width: 100%; text-align: center;">
            <table role="presentation" border="0" cellspacing="0" cellpadding="0" width="100%" style="width: 100%;">
              <tr>
                <td align="center" style="width: 100%;">
                  <table class="editorial-hero-card-img-table" role="presentation" border="0" cellspacing="0" cellpadding="0" width="${String(imageWidth).replace('px', '')}" style="width: ${imageWidth}; max-width: 100%;">
                    <tr>
                      <td align="center" style="width: 100%;">
                        <img src="${escapeHtml(imageUrl)}" alt="Hero" style="width: 100%; height: auto; display: block; border: 0;" />
                      </td>
                    </tr>
                  </table>
                  <table class="editorial-hero-card-panel" role="presentation" border="0" cellspacing="0" cellpadding="0" width="520" style="width: 90%; max-width: 520px; background-color: ${cardBackgroundColor}; margin: 0 auto; margin-top: ${cardOffset}; box-shadow: 0 10px 40px rgba(0,0,0,0.08); border-radius: 0px; position: relative; z-index: 10;">
                    <tr>
                      <td style="padding: 48px 40px; text-align: center;">
                        ${content.subtitle ? `
                          <p class="editorial-hero-card-subtitle" style="margin: 0 0 12px 0; font-size: 12px; font-weight: 700; color: ${subtitleColor}; text-transform: uppercase; letter-spacing: 0.2em; font-family: 'Inter', Helvetica, Arial, sans-serif;">
                            ${escapeHtml(content.subtitle)}
                          </p>
                        ` : ''}
                        <h1 class="editorial-hero-card-title" style="margin: 0 0 20px 0; font-size: ${headingFontSize}; font-weight: 800; color: ${textColor}; line-height: 1.1; font-family: 'Inter', Helvetica, Arial, sans-serif;">
                          ${escapeHtml(content.heading || "We're Bringing Basic Back")}
                        </h1>
                        ${content.text ? `
                          <p style="margin: 0 0 32px 0; font-size: 15px; color: ${bodyTextColor}; line-height: 1.6; font-family: 'Inter', Helvetica, Arial, sans-serif;">
                            ${escapeHtml(content.text)}
                          </p>
                        ` : ''}
                        ${(content.buttonText && content.showButton !== false) ? `
                          <div style="margin-top: 32px;">
                            <table role="presentation" border="0" cellspacing="0" cellpadding="0" align="center">
                              <tr>
                                <td align="center" bgcolor="${buttonColor}" style="padding: ${content.buttonPadding || '16px 48px'};">
                                  <a href="${escapeHtml(content.buttonUrl || '#')}" style="color: ${buttonTextColor}; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.15em; text-decoration: none; font-family: 'Inter', Helvetica, Arial, sans-serif; display: inline-block;">
                                    ${escapeHtml(content.buttonText)}
                                  </a>
                                </td>
                              </tr>
                            </table>
                          </div>
                        ` : ''}
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </div>
        `;
      }

      if (template === 'image-overlay') {
        const overlayMobile = editorialMobile.overlay;
        const baseHeight = parseInt(String(styles.height), 10) || 600;
        const height = isMobile
          ? `${Math.max(overlayMobile.minHeight, Math.min(baseHeight * overlayMobile.heightMultiplier, overlayMobile.maxHeight))}px`
          : (styles.height || '600px');
        const baseFontSize = parseInt(String(styles.fontSize), 10) || 72;
        const fontSize = isMobile
          ? `${Math.max(overlayMobile.minFontSize, Math.min(baseFontSize * overlayMobile.fontScale, overlayMobile.maxFontSize))}px`
          : (styles.fontSize || '72px');
        const minHeight = isMobile ? `${overlayMobile.minHeight}px` : '600px';
        const supportingFontSize = isMobile ? overlayMobile.supportingFontSize : '18px';
        const textColor = styles.color || '#FFFFFF';
        const verticalAlign = styles.justifyContent || 'flex-end';
        const horizontalAlign = styles.textAlign || 'left';
        const imageUrl = content.image || 'https://images.unsplash.com/photo-1551698618-1fed5d9e1ff0?auto=format&fit=crop&w=1200&q=80';

        const lineHeight = styles.lineHeight || '0.85';
        const textShadow = styles.textShadow || 'none';
        const innerPadding = computePadding(
          styles,
          isMobile ? overlayMobile.innerPadding : '60px 40px'
        );
        const headingHtml = (content.heading || 'Add your heading here...').replace(/\n/g, '<br />');
        const textHtml = content.text ? content.text.replace(/\n/g, '<br />') : '';
        const overlayButtonConfig = resolveHeroOverlayButtonConfig(
          content,
          styles,
          styles.fontFamily ? `'${styles.fontFamily}', 'Inter', Helvetica, Arial, sans-serif` : fontFamily,
          isMobile
        );
        const overlayButtonHtml =
          content.buttonText && content.showButton !== false
            ? renderHeroOverlayButtonHtml(overlayButtonConfig, {
                align:
                  horizontalAlign === 'center'
                    ? 'center'
                    : horizontalAlign === 'right'
                      ? 'right'
                      : 'left',
                marginTop: isMobile ? '24px' : '32px',
              })
            : '';

        const vmlVerticalAlign = verticalAlign === 'flex-start' ? 'top' : verticalAlign === 'center' ? 'middle' : 'bottom';
        const vmlHorizontalAlign = horizontalAlign === 'center' ? 'center' : horizontalAlign === 'right' ? 'right' : 'left';

        return `
          <table role="presentation" border="0" cellspacing="0" cellpadding="0" width="100%" style="width: 100%;">
            <tr>
              <td class="editorial-hero-overlay-bg" background="${escapeHtml(imageUrl)}" bgcolor="#000000" width="100%" height="${height}" valign="${vmlVerticalAlign}" style="background-image: url(${escapeHtml(imageUrl)}); background-size: cover; background-position: center center; height: ${height}; min-height: ${minHeight};">
                <!--[if gte mso 9]>
                <v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width: 600px; height: ${height};">
                  <v:fill type="tile" src="${escapeHtml(imageUrl)}" color="#000000" />
                  <v:textbox inset="0,0,0,0">
                <![endif]-->
                <div>
                  <table role="presentation" border="0" cellspacing="0" cellpadding="0" width="100%" style="width: 100%;">
                    <tr>
                      <td class="editorial-hero-overlay-inner" valign="${vmlVerticalAlign}" style="padding: ${innerPadding};">
                        <table role="presentation" border="0" cellspacing="0" cellpadding="0" width="100%" style="width: 100%;">
                          <tr>
                            <td align="${vmlHorizontalAlign}" valign="${vmlVerticalAlign}" style="width: 100%;">
                              <h1 class="editorial-hero-overlay-title" style="margin: 0; color: ${textColor}; font-family: ${styles.fontFamily ? `'${styles.fontFamily}', ` : ''}'Inter', Helvetica, Arial, sans-serif; font-size: ${fontSize}; font-weight: ${styles.fontWeight || '900'}; line-height: ${lineHeight}; text-align: ${horizontalAlign}; text-shadow: ${textShadow}; word-break: break-word;">
                                ${headingHtml}
                              </h1>
                              ${textHtml ? `
                                <p class="editorial-hero-overlay-text" style="margin: 16px 0 0 0; color: ${textColor}; font-family: ${styles.fontFamily ? `'${styles.fontFamily}', ` : ''}'Inter', Helvetica, Arial, sans-serif; font-size: ${supportingFontSize}; line-height: 1.5; text-align: ${horizontalAlign}; text-shadow: ${textShadow};">
                                  ${textHtml}
                                </p>
                              ` : ''}
                              ${overlayButtonHtml}
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>
                </div>
                <!--[if gte mso 9]>
                  </v:textbox>
                </v:rect>
                <![endif]-->
              </td>
            </tr>
          </table>
        `;
      }

      if (template === 'editorial') {
        const padding = computePadding(styles, '80px 40px');
        const headingFontSize = '56px';
        const textFontSize = '18px';
        const serifFont = "'Playfair Display', Georgia, serif";
        const subtitleHtml = content.subtitle
          ? `<p style="margin: 0 0 16px 0; font-size: 12px; font-weight: 600; color: #6B7280; text-transform: uppercase; letter-spacing: 0.3em; font-family: ${fontFamily}; text-align: center;">${escapeHtml(content.subtitle)}</p>`
          : '';
        const headingHtml = content.heading
          ? `<h1 class="editorial-hero-editorial-heading" style="margin: 0 0 24px 0; font-size: ${headingFontSize}; font-weight: 400; color: #111827; line-height: 1.1; font-family: ${serifFont}; text-align: center;">${escapeHtml(content.heading)}</h1>`
          : '';
        const imageHtml = content.image
          ? `<div class="editorial-hero-editorial-img" style="margin: 32px 0; text-align: center;"><img src="${escapeHtml(content.image)}" alt="Hero" style="width: 100%; height: auto; display: block; border-radius: 2px; border: 0;" /></div>`
          : '';
        const textHtml = content.text
          ? `<div style="max-width: 600px; margin: 0 auto;"><p class="editorial-hero-editorial-text" style="margin: 0 0 32px 0; font-size: ${textFontSize}; color: #4B5563; line-height: 1.7; font-family: ${fontFamily}; text-align: center;">${escapeHtml(content.text)}</p></div>`
          : '';
        const buttonHtml = (content.buttonText && content.showButton !== false)
          ? `<div style="text-align: center;"><a href="${escapeHtml(content.buttonUrl || '#')}" style="display: inline-block; background-color: #111827; color: #FFFFFF; padding: ${content.buttonPadding || '16px 40px'}; border-radius: 0; font-size: 13px; font-weight: 600; text-decoration: none; text-transform: uppercase; letter-spacing: 0.15em;">${escapeHtml(content.buttonText)}</a></div>`
          : '';

        return `<div class="editorial-hero-editorial" style="padding: ${padding}; background-color: ${styles.backgroundColor || '#FFFFFF'}; width: 100%; box-sizing: border-box;">${subtitleHtml}${headingHtml}${imageHtml}${textHtml}${buttonHtml}</div>`;
      }

      if (template === 'quote') {
        const padding = computePadding(styles, '80px 20px');
        const quoteFontSize = '42px';
        const serifFont = "'Playfair Display', Georgia, serif";
        const quoteHtml = `<blockquote class="editorial-quote-text" style="margin: 0 0 32px 0; font-size: ${quoteFontSize}; font-weight: 400; color: #111827; line-height: 1.3; font-family: ${serifFont}; font-style: italic; text-align: center;">"${escapeHtml(content.text || '')}"</blockquote>`;
        const citeHtml = content.subtitle
          ? `<cite style="display: block; font-size: 12px; font-weight: 600; color: #6B7280; text-transform: uppercase; letter-spacing: 0.2em; font-family: ${fontFamily}; font-style: normal; text-align: center;">— ${escapeHtml(content.subtitle)}</cite>`
          : '';
        return `
          <div class="editorial-quote-block" style="padding: ${padding}; background-color: ${styles.backgroundColor || '#FFFFFF'}; text-align: center; width: 100%; box-sizing: border-box;">
            <div class="editorial-quote-divider" style="width: 40px; height: 2px; background-color: #E5E7EB; margin: 0 auto 32px;"></div>
            ${quoteHtml}
            ${citeHtml}
          </div>
        `;
      }

      return '';
    }

    case 'image-content': {
      const template = content.template || 'standard';
      const isMobile = options.isMobile === true;
      if (template === 'standard') {
        const imagePadding = isMobile ? '20px' : '32px';
        const contentPadding = isMobile ? '20px' : '32px';
        const headingFontSize = isMobile ? '22px' : '26px';
        const textFontSize = isMobile ? '13px' : '14px';
        const buttonFontSize = isMobile ? '13px' : '14px';
        const subtitleFontSize = isMobile ? '12px' : '13px';
        const buttonPadding = isMobile ? '12px 20px' : '12px 24px';

        const imageHtml = content.image
          ? `<img src="${escapeHtml(content.image)}" alt="Content" style="width: 100%; height: auto; display: block;" />`
          : `<div style="width: 100%; height: 200px; background-color: #E5E7EB; border-radius: 8px; display: flex; align-items: center; justify-content: center;"><svg style="width: 48px; height: 48px; color: #9CA3AF;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>`;
        const subtitleHtml = content.subtitle
          ? `<p style="margin: 0 0 8px 0; font-size: ${subtitleFontSize}; font-weight: 500; color: #6B7280; text-transform: uppercase; letter-spacing: 0.08em; font-family: ${fontFamily};">${escapeHtml(content.subtitle)}</p>`
          : '';
        const headingHtml = content.heading
          ? `<h2 style="margin: 0 0 12px 0; font-size: ${headingFontSize}; font-weight: 700; color: #111827; font-family: ${fontFamily};">${escapeHtml(content.heading)}</h2>`
          : '';
        const textHtml = content.text
          ? `<p style="margin: 0 0 20px 0; font-size: ${textFontSize}; color: #6B7280; line-height: 160%; font-family: ${fontFamily};">${escapeHtml(content.text)}</p>`
          : '';
        const buttonHtml = content.buttonText
          ? `<a href="${escapeHtml(content.buttonUrl || '#')}" style="display: inline-block; background-color: #10B981; color: #FFFFFF; padding: ${buttonPadding}; border-radius: 9999px; font-size: ${buttonFontSize}; font-weight: 600; text-decoration: none;">${escapeHtml(content.buttonText)}</a>`
          : '';

        if (isMobile) {
          return `
            <div style="padding: ${styles.padding || '0'}; background-color: ${styles.backgroundColor || '#FFFFFF'}; font-family: ${fontFamily}; width: 100%; box-sizing: border-box;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td align="center" valign="top" style="padding: ${imagePadding}; background-color: #F3F4F6; text-align: center; width: 100%;">${imageHtml}</td>
                </tr>
                <tr>
                  <td align="left" valign="top" style="padding: ${contentPadding}; background-color: #FFFFFF; width: 100%;">${subtitleHtml}${headingHtml}${textHtml}${buttonHtml}</td>
                </tr>
              </table>
            </div>
          `;
        }

        return `
          <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="padding: ${styles.padding || '0'}; background-color: ${styles.backgroundColor || '#FFFFFF'}; width: 100%; box-sizing: border-box;">
            <tr>
              <td align="center" valign="top" style="padding: ${imagePadding}; background-color: #F3F4F6; text-align: center; width: 50%; max-width: 50%; vertical-align: top; box-sizing: border-box;">${imageHtml}</td>
              <td align="left" valign="top" style="padding: ${contentPadding}; background-color: #FFFFFF; width: 50%; max-width: 50%; vertical-align: top; box-sizing: border-box;">${subtitleHtml}${headingHtml}${textHtml}${buttonHtml}</td>
            </tr>
          </table>
        `;
      }

      if (template === 'editorial') {
        const padding = computePadding(styles, '0px');
        const headingFontSize = '48px';
        const textFontSize = '17px';
        const serifFont = "'Playfair Display', Georgia, serif";

        const subtitleHtml = content.subtitle
          ? `<p class="editorial-split-subtitle" style="margin: 0 0 12px 0; font-size: 12px; font-weight: 600; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.2em; font-family: ${fontFamily};">${escapeHtml(content.subtitle)}</p>`
          : '';
        const headingHtml = content.heading
          ? `<h2 class="editorial-split-heading" style="margin: 0 0 20px 0; font-size: ${headingFontSize}; font-weight: 400; color: #111827; line-height: 1.1; font-family: ${serifFont};">${escapeHtml(content.heading)}</h2>`
          : '';
        const textHtml = content.text
          ? `<p class="editorial-split-text" style="margin: 0 0 32px 0; font-size: ${textFontSize}; color: #4B5563; line-height: 1.7; font-family: ${fontFamily};">${escapeHtml(content.text)}</p>`
          : '';
        const buttonHtml = content.buttonText
          ? `<a href="${escapeHtml(content.buttonUrl || '#')}" style="display: inline-block; background-color: #111827; color: #FFFFFF; padding: 14px 32px; border-radius: 0; font-size: 13px; font-weight: 600; text-decoration: none; text-transform: uppercase; letter-spacing: 0.1em;">${escapeHtml(content.buttonText)}</a>`
          : '';
        const imageHtml = content.image
          ? `<img src="${escapeHtml(content.image)}" alt="Content" style="width: 100%; height: auto; display: block; border: 0;" />`
          : '';

        return `
          <table class="editorial-split" role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="padding: ${padding}; background-color: ${styles.backgroundColor || '#FFFFFF'}; width: 100%; max-width: 100%; table-layout: fixed; box-sizing: border-box;">
            <tr>
              <td class="editorial-split-col editorial-split-image" align="center" valign="top" width="50%" style="background-color: #F9FAFB; width: 50%; max-width: 50%; vertical-align: top; box-sizing: border-box;">${imageHtml}</td>
              <td class="editorial-split-col editorial-split-body" align="left" valign="middle" width="50%" style="padding: 60px 40px; width: 50%; max-width: 50%; background-color: #FFFFFF; vertical-align: middle; box-sizing: border-box;">${subtitleHtml}${headingHtml}${textHtml}${buttonHtml}</td>
            </tr>
          </table>
        `;
      }

      return '';
    }

    case 'feature-showcase': {
      const template = content.template || 'standard';
      const isMobile = options.isMobile === true;
      if (template === 'standard') {
        const containerPadding = isMobile ? '32px 20px' : '50px 20px';
        const imagePadding = isMobile ? '20px' : '24px';
        const contentPadding = isMobile ? '20px' : '8px';
        const headingFontSize = isMobile ? '20px' : '24px';
        const textFontSize = isMobile ? '14px' : '16px';
        const buttonFontSize = isMobile ? '13px' : '14px';
        const buttonPadding = isMobile ? '12px 20px' : '12px 24px';

        const imageHtml = content.image
          ? `<img src="${escapeHtml(content.image)}" alt="Feature" style="width: 100%; height: auto; display: block;" />`
          : `<div style="width: 100%; height: 200px; background-color: #E5E7EB; border-radius: 8px; display: flex; align-items: center; justify-content: center;"><svg style="width: 48px; height: 48px; color: #9CA3AF;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>`;
        const headingHtml = content.heading
          ? `<h2 style="margin: 0 0 12px 0; font-size: ${headingFontSize}; font-weight: 700; color: #111827; font-family: ${fontFamily};">${escapeHtml(content.heading)}</h2>`
          : '';
        const textHtml = content.text
          ? `<p style="margin: 0 0 25px 0; font-size: ${textFontSize}; color: #4B5563; line-height: 160%; font-family: ${fontFamily};">${escapeHtml(content.text)}</p>`
          : '';
        const buttonHtml = content.buttonText
          ? `<a href="${escapeHtml(content.buttonUrl || '#')}" style="display: inline-block; background-color: #111827; color: #FFFFFF; padding: ${buttonPadding}; border-radius: 9999px; font-size: ${buttonFontSize}; font-weight: 600; text-decoration: none;">${escapeHtml(content.buttonText)}</a>`
          : '';

        if (isMobile) {
          return `
            <div style="padding: ${styles.padding || containerPadding}; background-color: ${styles.backgroundColor || '#FFFFFF'}; font-family: ${fontFamily}; width: 100%; box-sizing: border-box;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td align="center" valign="top" style="padding: ${imagePadding}; background-color: #F9FAFB; text-align: center; width: 100%;">${imageHtml}</td>
                </tr>
                <tr>
                  <td align="left" valign="top" style="padding: ${contentPadding}; background-color: #FFFFFF; width: 100%;">${headingHtml}${textHtml}${buttonHtml}</td>
                </tr>
              </table>
            </div>
          `;
        }

        return `
          <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="padding: ${styles.padding || containerPadding}; background-color: ${styles.backgroundColor || '#FFFFFF'}; width: 100%; box-sizing: border-box;">
            <tr>
              <td align="center" valign="top" style="padding: ${imagePadding}; background-color: #F9FAFB; text-align: center; width: 45%; max-width: 45%; vertical-align: top; box-sizing: border-box;">${imageHtml}</td>
              <td align="left" valign="top" style="padding: ${contentPadding}; background-color: #FFFFFF; width: 55%; max-width: 55%; vertical-align: top; box-sizing: border-box;">${headingHtml}${textHtml}${buttonHtml}</td>
            </tr>
          </table>
        `;
      }
      return '';
    }

    // testimonial — handled by the full case below

    // cta-banner and testimonial — handled by the full cases below

    case 'profile-card': {
      const template = content.template || 'standard';
      const isMobile = options.isMobile === true;
      if (template === 'standard') {
        const profilePadding = isMobile ? '32px 24px' : '40px 32px';
        const contentPadding = isMobile ? '32px 24px' : '40px 32px';
        const nameFontSize = isMobile ? '16px' : '18px';
        const roleFontSize = isMobile ? '13px' : '14px';
        const eventTitleFontSize = isMobile ? '28px' : '36px';
        const eventTextFontSize = isMobile ? '15px' : '16px';
        const eventLabelFontSize = isMobile ? '11px' : '12px';
        const profileImageSize = isMobile ? '80px' : '100px';

        const profileImageHtml = content.profileImage
          ? `<img src="${escapeHtml(content.profileImage)}" alt="Profile" style="width: ${profileImageSize}; height: ${profileImageSize}; border-radius: 50%; object-fit: cover; margin-bottom: 20px;" />`
          : `<div style="width: ${profileImageSize}; height: ${profileImageSize}; border-radius: 50%; background-color: #F1F5F9; margin: 0 auto 20px; display: flex; align-items: center; justify-content: center; font-size: 32px; color: #64748B;">👤</div>`;

        const nameHtml = content.name
          ? `<h3 style="margin: 0 0 4px 0; font-size: ${nameFontSize}; font-weight: 800; color: #111827; text-transform: uppercase; font-family: 'Inter', sans-serif; letter-spacing: 0.05em;">${escapeHtml(content.name)}</h3>`
          : '';
        const roleHtml = content.role
          ? `<p style="margin: 0; font-size: ${roleFontSize}; color: #64748B; font-family: 'Inter', sans-serif;">${escapeHtml(content.role)}</p>`
          : '';
        const eventLabelHtml = content.eventLabel
          ? `<p style="margin: 0 0 12px 0; font-size: ${eventLabelFontSize}; font-weight: 800; color: #6366F1; text-transform: uppercase; letter-spacing: 0.1em; font-family: 'Inter', sans-serif;">${escapeHtml(content.eventLabel)}</p>`
          : '';
        const eventTitleHtml = content.eventTitle
          ? `<h2 style="margin: 0 0 16px 0; font-size: ${eventTitleFontSize}; font-weight: 850; color: #111827; font-family: 'Playfair Display', serif; line-height: 1.1; letter-spacing: -0.02em;">${escapeHtml(content.eventTitle)}</h2>`
          : '';
        const eventDescHtml = content.eventDescription
          ? `<p style="margin: 0 0 32px 0; font-size: ${eventTextFontSize}; color: #4B5563; line-height: 1.6; font-family: 'Inter', sans-serif;">${escapeHtml(content.eventDescription)}</p>`
          : '';
        const buttonHtml = content.buttonText
          ? `<a href="${escapeHtml(content.buttonUrl || '#')}" style="display: inline-block; background-color: #6366F1; color: #FFFFFF; padding: 14px 32px; border-radius: 12px; font-size: 15px; font-weight: 700; text-decoration: none; font-family: 'Inter', sans-serif;">${escapeHtml(content.buttonText)}</a>`
          : '';

        if (isMobile) {
          return `
            <div style="padding: ${styles.padding || '0'}; background-color: ${styles.backgroundColor || '#FFFFFF'}; font-family: 'Inter', sans-serif; width: 100%; box-sizing: border-box;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td align="center" valign="top" style="padding: ${profilePadding}; background-color: #F8FAFC; text-align: center; width: 100%; border-radius: 24px 24px 0 0; border: 1px solid #F1F5F9; border-bottom: none;">${profileImageHtml}${nameHtml}${roleHtml}</td>
                </tr>
                <tr>
                  <td align="left" valign="top" style="padding: ${contentPadding}; background-color: #FFFFFF; width: 100%; border-radius: 0 0 24px 24px; border: 1px solid #F1F5F9;">${eventLabelHtml}${eventTitleHtml}${eventDescHtml}${buttonHtml}</td>
                </tr>
              </table>
            </div>
          `;
        }

        return `
          <div style="padding: ${styles.padding || '0'}; background-color: ${styles.backgroundColor || '#FFFFFF'}; font-family: 'Inter', sans-serif; width: 100%; box-sizing: border-box;">
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="border-radius: 32px; overflow: hidden; border: 1px solid #F1F5F9;">
              <tr>
                <td align="center" valign="middle" style="padding: ${profilePadding}; background-color: #F8FAFC; text-align: center; width: 35%; max-width: 35%; vertical-align: middle; box-sizing: border-box; border-right: 1px solid #F1F5F9;">${profileImageHtml}${nameHtml}${roleHtml}</td>
                <td align="left" valign="top" style="padding: ${contentPadding}; background-color: #FFFFFF; width: 65%; max-width: 65%; vertical-align: top; box-sizing: border-box;">${eventLabelHtml}${eventTitleHtml}${eventDescHtml}${buttonHtml}</td>
              </tr>
            </table>
          </div>
        `;
      }
      return '';
    }

    case 'event-announcement': {
      const layout = content.layout || 'standard';
      const isMobile = options.isMobile === true;
      const bgColor = styles.backgroundColor || (layout === 'webinar' ? '#FFFFFF' : '#0F172A');
      const textColor = layout === 'webinar' ? '#111827' : '#F9FAFB';
      const mutedColor = layout === 'webinar' ? '#6B7280' : '#9CA3AF';
      const accentColor = layout === 'webinar' ? '#6366F1' : '#10B981';

      const heroImageHtml = content.heroImage
        ? `
        <tr>
          <td style="padding: 0; position: relative;">
            <img src="${escapeHtml(content.heroImage)}" alt="${escapeHtml(content.title || 'Event')}" style="width: 100%; height: auto; display: block; max-height: ${layout === 'webinar' ? '450px' : '500px'}; object-fit: cover;" />
            <div style="position: absolute; top: 20px; right: 20px; background-color: ${accentColor}; color: #FFFFFF; padding: 8px 16px; border-radius: 999px; font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em;">${layout === 'webinar' ? 'Live Webinar' : 'Event'}</div>
          </td>
        </tr>
      `
        : '';

      const contentPadding = isMobile
        ? (layout === 'webinar' ? '40px 24px' : '60px 24px')
        : (layout === 'webinar' ? '60px 48px' : '80px 48px');

      const detailsHtml =
        content.showDetails && (content.eventDate || content.eventTime || content.eventLocation)
          ? `
        <tr>
          <td style="padding: 0 48px 48px 48px;">
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: ${layout === 'webinar' ? '#F8FAFC' : 'rgba(255,255,255,0.03)'}; border-radius: 24px; border: 1px solid ${layout === 'webinar' ? '#F1F5F9' : 'rgba(255,255,255,0.05)'};">
              <tr>
                <td style="padding: 32px; text-align: left;">
                  ${content.eventDate ? `<div style="margin-bottom: 16px;"><div style="font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; color: ${accentColor}; margin-bottom: 4px;">Date</div><div style="font-size: 16px; font-weight: 600; color: ${textColor};">${escapeHtml(content.eventDate)}</div></div>` : ''}
                  ${content.eventTime ? `<div style="margin-bottom: 16px;"><div style="font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; color: ${accentColor}; margin-bottom: 4px;">Time</div><div style="font-size: 16px; font-weight: 600; color: ${textColor};">${escapeHtml(content.eventTime)}</div></div>` : ''}
                  ${content.eventLocation ? `<div><div style="font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; color: ${accentColor}; margin-bottom: 4px;">Location</div><div style="font-size: 16px; font-weight: 600; color: ${textColor};">${escapeHtml(content.eventLocation)}</div></div>` : ''}
                </td>
              </tr>
            </table>
          </td>
        </tr>
      `
          : '';

      return `
      <div style="padding: ${styles.padding || '0'}; background-color: ${bgColor}; font-family: 'Inter', system-ui, sans-serif; color: ${textColor}; width: 100%; box-sizing: border-box;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
          ${heroImageHtml}
          <tr>
            <td align="center" style="padding: ${contentPadding}; text-align: ${layout === 'webinar' ? 'left' : 'center'};">
              ${content.title ? `<h1 style="margin: 0 0 20px 0; font-size: ${isMobile ? '32px' : '48px'}; font-weight: 850; color: ${textColor}; font-family: 'Playfair Display', serif; line-height: 1.1; letter-spacing: -0.02em;">${escapeHtml(content.title)}</h1>` : ''}
              ${content.description ? `<p style="margin: 0 0 32px 0; font-size: ${isMobile ? '16px' : '18px'}; line-height: 1.6; color: ${mutedColor}; max-width: 700px; ${layout === 'webinar' ? '' : 'margin-left: auto; margin-right: auto;'}">${escapeHtml(content.description)}</p>` : ''}
              ${content.buttonText ? `<a href="${escapeHtml(content.buttonUrl || '#')}" style="display: inline-block; padding: 18px 48px; border-radius: 16px; background-color: ${accentColor}; color: #FFFFFF; font-weight: 700; font-size: 16px; text-decoration: none;">${escapeHtml(content.buttonText)}</a>` : ''}
            </td>
          </tr>
          ${detailsHtml}
        </table>
      </div>
    `;
    }

    case 'testimonial':
      // Single source of truth: renderTestimonialHtml() — identical output in canvas & email
      return renderTestimonialHtml(content, styles, fontFamily, options.isMobile === true);

    case 'cta-banner': {
      const isMobile = options.isMobile === true;
      const heading = content.heading || 'Ready to Start?';
      const text = content.text || '';
      const buttonText = content.buttonText || 'Get Started';
      const buttonUrl = content.buttonUrl || '#';
      const bgColor = styles.backgroundColor || '#6366F1';
      const textColor = styles.color || '#FFFFFF';

      return `
      <div style="padding: ${styles.padding || (isMobile ? '60px 20px' : '100px 40px')}; background-color: ${bgColor}; font-family: 'Inter', system-ui, sans-serif; text-align: center; width: 100%; box-sizing: border-box;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
          <tr>
            <td align="center">
              <h2 style="margin: 0 0 20px 0; font-size: ${isMobile ? '32px' : '48px'}; font-weight: 850; color: ${textColor}; font-family: 'Playfair Display', serif; line-height: 1.1; letter-spacing: -0.03em;">
                ${escapeHtml(heading)}
              </h2>
              ${text ? `<p style="margin: 0 0 40px 0; font-size: ${isMobile ? '16px' : '20px'}; line-height: 1.6; color: ${textColor}; opacity: 0.9; font-weight: 400; max-width: 700px; margin-left: auto; margin-right: auto;">${escapeHtml(text)}</p>` : ''}
              ${buttonText ? `
                <a href="${escapeHtml(buttonUrl)}" style="display: inline-block; padding: 14px 32px; border-radius: 12px; background-color: #FFFFFF; color: ${bgColor}; font-weight: 700; font-size: 16px; text-decoration: none;">${escapeHtml(buttonText)}</a>
              ` : ''}
            </td>
          </tr>
        </table>
      </div>
    `;
    }

    case 'pricing-table': {
      const plans = Array.isArray(content.plans) ? content.plans.filter((p: any) => p && p.enabled !== false) : [];
      const isMobile = options.isMobile === true;
      const cols = isMobile ? 1 : Math.min(plans.length, 3);
      const colWidth = isMobile ? 100 : Math.floor(100 / cols);

      const planCardHtml = (plan: any) => `
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: ${plan.highlighted ? '#FFFFFF' : '#F9FAFB'}; border-radius: 24px; border: ${plan.highlighted ? '2px solid #6366F1' : '1px solid #E5E7EB'}; width: 100%; margin-bottom: ${isMobile ? '24px' : '0'}; box-sizing: border-box; ${plan.highlighted ? 'box-shadow: 0 20px 25px -5px rgba(99, 102, 241, 0.1);' : ''}">
          <tr>
            <td style="padding: ${isMobile ? '32px 20px' : '32px 20px'}; text-align: center; box-sizing: border-box; position: relative;">
              ${plan.highlighted ? `
                <div style="margin-top: -44px; margin-bottom: 24px; text-align: center;">
                  <span style="background-color: #6366F1; color: #FFFFFF; padding: 3px 10px; border-radius: 999px; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; display: inline-block;">MOST POPULAR</span>
                </div>
              ` : ''}
              
              <div style="margin-bottom: 24px;">
                ${plan.name ? `<div style="font-size: 13px; font-weight: 700; color: ${plan.highlighted ? '#6366F1' : '#64748B'}; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">${escapeHtml(plan.name)}</div>` : ''}
                ${plan.price ? `
                  <div style="font-size: ${isMobile ? '28px' : '32px'}; font-weight: 850; color: #111827; letter-spacing: -0.03em; margin-bottom: 8px;">
                    ${escapeHtml(plan.price)}${plan.period ? `<span style="font-size: 13px; font-weight: 400; color: #94A3B8; margin-left: 4px;">/${escapeHtml(plan.period)}</span>` : ''}
                  </div>` : ''}
                ${plan.description ? `<div style="font-size: 13px; color: #64748B; line-height: 1.4; margin-top: 8px;">${escapeHtml(plan.description)}</div>` : ''}
              </div>

              <div style="margin-bottom: 32px; text-align: left;">
                ${Array.isArray(plan.features) && plan.features.length > 0 ? `
                  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                    ${plan.features.map((f: string) => `
                      <tr>
                        <td style="padding-bottom: 10px; vertical-align: top; width: 22px;">
                          <span style="color: ${plan.highlighted ? '#6366F1' : '#10B981'}; font-size: 14px; line-height: 1;">✓</span>
                        </td>
                        <td style="padding-bottom: 10px; font-size: 12px; color: #475569; line-height: 1.4;">
                          ${escapeHtml(f)}
                        </td>
                      </tr>
                    `).join('')}
                  </table>
                ` : ''}
              </div>

              ${plan.buttonText ? `
                <a href="${escapeHtml(plan.buttonUrl || '#')}" style="display: block; padding: 12px 16px; border-radius: 10px; background-color: ${plan.highlighted ? '#6366F1' : '#111827'}; color: #FFFFFF; font-weight: 700; font-size: 13px; text-decoration: none; text-align: center;">${escapeHtml(plan.buttonText)}</a>
              ` : ''}
            </td>
          </tr>
        </table>
      `;

      const plansHtml = isMobile
        ? plans.map((plan: any) => `<tr><td align="center" valign="top" style="padding: 0; width: 100%; box-sizing: border-box;">${planCardHtml(plan)}</td></tr>`).join('')
        : `<tr>${plans.map((plan: any) => `<td align="center" valign="top" style="padding: 0 10px; width: ${colWidth}%; vertical-align: top; box-sizing: border-box;">${planCardHtml(plan)}</td>`).join('')}</tr>`;

      return `
      <div style="padding: ${styles.padding || '0'}; background-color: ${styles.backgroundColor || '#FFFFFF'}; font-family: 'Inter', system-ui, -apple-system, sans-serif; width: 100%; box-sizing: border-box;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="width: 100%;">
          ${content.sectionTitle ? `
            <tr>
              <td align="center" style="padding: ${isMobile ? '40px 20px 20px 20px' : '48px 20px 32px 20px'}; font-size: ${isMobile ? '24px' : '32px'}; font-weight: 800; color: #111827; text-align: center; font-family: 'Playfair Display', serif; letter-spacing: -0.02em;">
                ${escapeHtml(content.sectionTitle)}
              </td>
            </tr>` : ''}
          <tr>
            <td align="center" style="padding: ${isMobile ? '0 20px 40px 20px' : '0 20px 60px 20px'}; width: 100%; box-sizing: border-box;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="width: 100%; max-width: ${isMobile ? '100%' : '1100px'}; margin: 0 auto;">
                ${plansHtml}
              </table>
            </td>
          </tr>
        </table>
      </div>
    `;
    }

    case 'features-grid': {
      const gridContent = normalizeFeaturesGridContent(content);
      const features = Array.isArray(gridContent.features)
        ? gridContent.features.filter((f: any) => f && f.enabled !== false)
        : [];
      const isMobile = options.isMobile === true;
      const layoutVariant = resolveFeaturesGridVariant(gridContent);
      const meta = getFeaturesGridLayoutMeta(layoutVariant);
      const isDark = layoutVariant === 'dark-band';
      const bgColor = isDark ? '#111827' : styles.backgroundColor || '#FFFFFF';
      const titleColor = isDark ? '#F9FAFB' : '#111827';
      const columns = getFeaturesGridColumns(layoutVariant, isMobile);
      const colWidth = isMobile && layoutVariant !== 'icon-list' ? `${100 / columns}%` : `${100 / columns}%`;
      const cellAlign =
        layoutVariant === 'icon-2x2' || layoutVariant === 'icon-list' || layoutVariant === 'numbered'
          ? 'left'
          : 'center';
      const titlePad = getFeaturesGridTitlePadding(layoutVariant, isMobile);
      const gridPad = getFeaturesGridBodyPadding(layoutVariant, isMobile);
      const maxWidth =
        layoutVariant === 'icon-list' ? '720px' : layoutVariant === 'compact-4' ? '100%' : '1100px';
      const titleFontSize =
        layoutVariant === 'icon-2x2' || layoutVariant === 'icon-list'
          ? isMobile
            ? '28px'
            : '36px'
          : layoutVariant === 'compact-4' || layoutVariant === 'numbered'
            ? isMobile
              ? '26px'
              : '32px'
            : isMobile
              ? '32px'
              : '42px';

      const featureHtml = (feature: any, index: number) =>
        renderFeaturesGridFeatureEmailHtml(feature, layoutVariant, isMobile, index, {
          textColor: isDark ? '#F9FAFB' : '#111827',
          mutedColor: isDark ? '#9CA3AF' : layoutVariant === 'cards' ? '#64748B' : '#374151',
        });

      let featuresBodyHtml = '';
      if (layoutVariant === 'icon-list') {
        featuresBodyHtml = features.map((f: any, i: number) => featureHtml(f, i)).join('');
      } else {
        const rows: any[][] = [];
        for (let i = 0; i < features.length; i += columns) {
          rows.push(features.slice(i, i + columns));
        }
        featuresBodyHtml = rows
          .map((row, rowIndex) => {
            const cells = row
              .map(
                (feature: any, cellIndex: number) => `
            <td align="${cellAlign}" valign="top" style="padding: ${layoutVariant === 'compact-4' ? (isMobile ? '0 6px 16px 6px' : '0 12px 24px 12px') : layoutVariant === 'icon-2x2' ? '0 20px 32px 0' : '0 16px'}; width: ${colWidth}; vertical-align: top; box-sizing: border-box;">
              ${featureHtml(feature, rowIndex * columns + cellIndex)}
            </td>`
              )
              .join('');
            const empty = columns - row.length;
            const emptyCells =
              empty > 0
                ? Array(empty)
                    .fill(`<td width="${colWidth}" style="padding: 0 16px;">&nbsp;</td>`)
                    .join('')
                : '';
            return `<tr>${cells}${emptyCells}</tr>`;
          })
          .join('');
        featuresBodyHtml = `<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="width: 100%; max-width: ${maxWidth}; margin: 0 auto;">${featuresBodyHtml}</table>`;
      }

      return `
      <div style="padding: ${computePadding(styles, '0')}; background-color: ${bgColor}; font-family: 'Inter', system-ui, sans-serif; width: 100%; box-sizing: border-box;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="width: 100%;">
          ${gridContent.sectionTitle ? `
            <tr>
              <td align="${meta.titleAlign}" style="padding: ${titlePad}; font-size: ${titleFontSize}; font-weight: ${meta.titleWeight}; color: ${titleColor}; text-align: ${meta.titleAlign}; font-family: ${meta.titleFontFamily}; letter-spacing: -0.02em; line-height: 1.15;">
                ${escapeHtml(gridContent.sectionTitle)}
              </td>
            </tr>` : ''}
          <tr>
            <td align="${meta.titleAlign}" style="padding: ${gridPad}; width: 100%; box-sizing: border-box;">
              ${layoutVariant === 'icon-list'
                ? `<div style="max-width: ${maxWidth}; margin: 0 auto;">${featuresBodyHtml}</div>`
                : featuresBodyHtml}
            </td>
          </tr>
        </table>
      </div>
    `;
    }

    case 'promo-banner': {
      const isMobile = options.isMobile === true;
      const bgColor = styles.backgroundColor || '#DC2626';
      const textColor = '#FFFFFF';

      const heroImageHtml = content.heroImage
        ? `
        <tr>
          <td style="padding: 0;">
            <img src="${escapeHtml(content.heroImage)}" alt="${escapeHtml(content.title || 'Promotion')}" style="width: 100%; height: auto; display: block; max-height: 400px; object-fit: cover;" />
          </td>
        </tr>
      `
        : '';

      return `
      <div style="padding: ${styles.padding || '0'}; background-color: ${bgColor}; font-family: 'Inter', system-ui, sans-serif; color: ${textColor}; width: 100%; box-sizing: border-box;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
          ${heroImageHtml}
          <tr>
            <td align="center" style="padding: ${isMobile ? '48px 20px' : '64px 20px'}; text-align: center;">
              ${content.title ? `<h1 style="margin: 0 0 16px 0; font-size: ${isMobile ? '32px' : '48px'}; font-weight: 850; color: ${textColor}; font-family: 'Playfair Display', serif; line-height: 1.1; letter-spacing: -0.02em;">${escapeHtml(content.title)}</h1>` : ''}
              ${content.description ? `<p style="margin: 0 0 32px 0; font-size: ${isMobile ? '16px' : '18px'}; line-height: 1.6; color: ${textColor}; opacity: 0.9; max-width: 700px; margin-left: auto; margin-right: auto;">${escapeHtml(content.description)}</p>` : ''}
              ${content.buttonText ? `<a href="${escapeHtml(content.buttonUrl || '#')}" style="display: inline-block; padding: 14px 32px; border-radius: 12px; background-color: #FFFFFF; color: ${bgColor}; font-weight: 700; font-size: 16px; text-decoration: none;">${escapeHtml(content.buttonText)}</a>` : ''}
            </td>
          </tr>
        </table>
      </div>
    `;
    }

    case 'blog-preview': {
      const isMobile = options.isMobile === true;
      const imageHtml =
        content.showImage && content.image
          ? `
        <tr>
          <td style="padding: 0;">
            <img src="${escapeHtml(content.image)}" alt="${escapeHtml(content.title || 'Blog post')}" style="width: 100%; height: auto; display: block; max-height: 400px; object-fit: cover;" />
          </td>
        </tr>
      `
          : '';

      const contentPadding = isMobile ? '32px 24px' : '48px 40px';

      return `
      <div style="padding: ${styles.padding || '0'}; background-color: ${styles.backgroundColor || '#FFFFFF'}; font-family: 'Inter', system-ui, sans-serif; width: 100%; box-sizing: border-box;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
          ${imageHtml}
          <tr>
            <td style="padding: ${contentPadding};">
              ${content.showCategory && content.category ? `<div style="font-size: 13px; font-weight: 800; color: #6366F1; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 12px;">${escapeHtml(content.category)}</div>` : ''}
              ${content.title ? `<h2 style="margin: 0 0 16px 0; font-size: ${isMobile ? '28px' : '36px'}; font-weight: 850; color: #111827; font-family: 'Playfair Display', serif; line-height: 1.2; letter-spacing: -0.01em;">${escapeHtml(content.title)}</h2>` : ''}
              ${content.excerpt ? `<p style="margin: 0 0 32px 0; font-size: ${isMobile ? '16px' : '18px'}; line-height: 1.6; color: #4B5563;">${escapeHtml(content.excerpt)}</p>` : ''}
              ${content.buttonText ? `<a href="${escapeHtml(content.buttonUrl || '#')}" style="display: inline-block; padding: 14px 32px; border-radius: 12px; background-color: #6366F1; color: #FFFFFF; font-weight: 700; font-size: 15px; text-decoration: none;">${escapeHtml(content.buttonText)}</a>` : ''}
            </td>
          </tr>
        </table>
      </div>
    `;
    }

    case 'faq-section': {
      const isMobile = options.isMobile === true;
      const faqs = Array.isArray(content.faqs) ? content.faqs.filter((f: any) => f && f.enabled !== false) : [];

      const faqsHtml = faqs
        .map(
          (faq: any, index: number) => `
          <tr>
            <td style="padding: ${isMobile ? '24px 0' : '32px 0'}; border-bottom: ${index < faqs.length - 1 ? '1px solid #F1F5F9' : 'none'};">
              ${faq.question ? `<div style="font-size: ${isMobile ? '18px' : '20px'}; font-weight: 800; color: #111827; margin-bottom: 12px; letter-spacing: -0.01em;">${escapeHtml(faq.question)}</div>` : ''}
              ${faq.answer ? `<div style="font-size: ${isMobile ? '15px' : '16px'}; line-height: 1.6; color: #64748B;">${escapeHtml(faq.answer)}</div>` : ''}
            </td>
          </tr>
        `
        )
        .join('');

      return `
      <div style="padding: ${styles.padding || '0'}; background-color: ${styles.backgroundColor || '#FFFFFF'}; font-family: 'Inter', system-ui, sans-serif; width: 100%; box-sizing: border-box;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
          ${content.sectionTitle ? `
            <tr>
              <td align="center" style="padding: ${isMobile ? '60px 20px 32px 20px' : '80px 20px 48px 20px'}; font-size: ${isMobile ? '32px' : '42px'}; font-weight: 850; color: #111827; text-align: center; font-family: 'Playfair Display', serif; letter-spacing: -0.02em;">
                ${escapeHtml(content.sectionTitle)}
              </td>
            </tr>` : ''}
          <tr>
            <td align="center" style="padding: ${isMobile ? '0 24px 60px 24px' : '0 40px 80px 40px'};">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="max-width: 800px; margin: 0 auto;">
                ${faqsHtml}
              </table>
            </td>
          </tr>
        </table>
      </div>
    `;
    }

    case 'team-grid': {
      const isMobile = options.isMobile === true;
      const members = Array.isArray(content.members)
        ? content.members.filter((m: any) => m && m.enabled !== false)
        : [];
      const columns = isMobile ? 1 : 3;
      const colWidth = isMobile ? '100%' : '33.33%';

      const rows = [];
      const rowSize = columns;
      for (let i = 0; i < members.length; i += rowSize) {
        rows.push(members.slice(i, i + rowSize));
      }

      const titlePadding = isMobile ? '60px 24px 32px 24px' : '80px 40px 48px 40px';
      const titleFontSize = isMobile ? '32px' : '42px';
      const membersContainerPadding = isMobile ? '0 24px 60px 24px' : '0 40px 80px 40px';
      const memberPadding = isMobile ? '0 0 32px 0' : '0 16px 32px 16px';
      const imageSize = isMobile ? '100px' : '150px';
      const nameFontSize = isMobile ? '18px' : '20px';
      const roleFontSize = isMobile ? '14px' : '15px';

      const teamRowsHtml = rows.map((row) => {
        const rowCells = row.map((member: any) => `
          <td align="center" valign="top" style="padding: ${memberPadding}; width: ${colWidth}; text-align: center; vertical-align: top;">
            ${member.image ? `<img src="${escapeHtml(member.image)}" alt="${escapeHtml(member.name)}" style="width: ${imageSize}; height: ${imageSize}; border-radius: 50%; object-fit: cover; margin: 0 auto 20px auto; display: block;" />` : ''}
            ${member.name ? `<div style="font-size: ${nameFontSize}; font-weight: 800; color: #111827; margin-bottom: 6px; letter-spacing: -0.01em;">${escapeHtml(member.name)}</div>` : ''}
            ${member.role ? `<div style="font-size: ${roleFontSize}; color: #64748B; font-weight: 400;">${escapeHtml(member.role)}</div>` : ''}
          </td>
        `).join('');

        const emptyCellsCount = rowSize - row.length;
        const emptyCellsStr = emptyCellsCount > 0
          ? Array(emptyCellsCount).fill(`<td width="${colWidth}" style="padding: ${memberPadding};">&nbsp;</td>`).join('')
          : '';

        return `<tr>${rowCells}${emptyCellsStr}</tr>`;
      }).join('');

      return `
      <div style="padding: ${styles.padding || '0'}; background-color: ${styles.backgroundColor || '#FFFFFF'}; font-family: 'Inter', system-ui, sans-serif; width: 100%; box-sizing: border-box;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
          ${content.sectionTitle ? `
            <tr>
              <td align="center" style="padding: ${titlePadding}; font-size: ${titleFontSize}; font-weight: 850; color: #111827; text-align: center; font-family: 'Playfair Display', serif; letter-spacing: -0.02em;">
                ${escapeHtml(content.sectionTitle)}
              </td>
            </tr>` : ''}
          <tr>
            <td align="center" style="padding: ${membersContainerPadding}; width: 100%; box-sizing: border-box;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="max-width: ${isMobile ? '100%' : '1100px'}; margin: 0 auto; width: 100%;">
                ${teamRowsHtml}
              </table>
            </td>
          </tr>
        </table>
      </div>
    `;
    }

    default:
      return '';
  }
}

function buildResponsiveCss(blocks: EmailBlockData[]): string {
  const rules = blocks
    .filter((block) => block.mobileStyles && Object.keys(block.mobileStyles).length > 0)
    .map((block) => {
      const mobile = block.mobileStyles as Record<string, string>;
      const declarations = Object.entries(mobile)
        .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
        .join(' ');
      const blockRule = declarations ? `.email-block-${block.id} { ${declarations} }` : '';

      let buttonAlignRule = '';
      if (block.type === 'button' && mobile.textAlign) {
        const align = mobile.textAlign;
        const margin =
          align === 'center'
            ? '0 auto !important'
            : align === 'right'
              ? '0 0 0 auto !important'
              : '0 !important';
        buttonAlignRule = `.email-block-${block.id} .button-block-wrap td { text-align: ${align} !important; } .email-block-${block.id} .button-block-wrap table { margin: ${margin}; }`;
      }

      return [blockRule, buttonAlignRule].filter(Boolean).join('\n');
    })
    .filter(Boolean)
    .join('\n');

  if (!rules) {
    return '';
  }

  return `@media only screen and (max-width: 600px) {\n${rules}\n}`;
}

function camelToKebab(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

function wrapBlock(blockId: string, html: string): string {
  if (!html.trim()) {
    return '';
  }
  return `<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="width: 100%; max-width: 100%; margin: 0; padding: 0; table-layout: fixed; box-sizing: border-box;">
    <tr>
      <td align="left" valign="top" style="padding: 0; width: 100%; max-width: 100%; box-sizing: border-box;">
        <div class="email-block-${blockId}" data-email-block-id="${blockId}" style="width: 100%; max-width: 100%; margin: 0; padding: 0; box-sizing: border-box; overflow-x: hidden;">
          ${html}
        </div>
      </td>
    </tr>
  </table>`;
}

// escapeHtml is now imported from helpers

type VideoEmbedData =
  | { type: 'youtube'; src: string; title: string }
  | { type: 'vimeo'; src: string; title: string }
  | { type: 'file'; src: string; title: string };

function getVideoEmbedData(url?: string): VideoEmbedData | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  const youtubeMatch = trimmed.match(/(?:youtu\.be\/|youtube\.com\/(?:shorts\/|embed\/|watch\?v=))([A-Za-z0-9_-]{11})/);
  if (youtubeMatch && youtubeMatch[1]) {
    return {
      type: 'youtube',
      src: `https://www.youtube.com/embed/${youtubeMatch[1]}?rel=0`,
      title: 'YouTube video player',
    };
  }

  const vimeoMatch = trimmed.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch && vimeoMatch[1]) {
    return {
      type: 'vimeo',
      src: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
      title: 'Vimeo video player',
    };
  }

  if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(trimmed)) {
    return {
      type: 'file',
      src: trimmed,
      title: 'Hosted video player',
    };
  }

  return null;
}

